import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './styles/responsive-utilities.css'; // E-commerce responsive utilities
import './pages/ServicesHomePage.css';
import { apiService, Product as ApiProduct, Coupon } from './services/api';
import { useAuth } from './contexts/AuthContext';
import { useRef } from 'react';
import { AIAssistantIntegration } from './components/AIAssistant';
import MultiShopBrowser from './components/MultiShopBrowser';
import CrossShopCart from './components/CrossShopCart';
import ShopDiscovery from './components/ShopDiscovery';
import ShopDetails from './components/ShopDetails';
import OrderPlacement from './components/OrderPlacement';
import AdvancedSearch from './components/AdvancedSearch';
import SearchResults from './components/SearchResults';
import { Shop, ShopProduct } from './services/shopService';
import CustomerHeader from './components/CustomerHeader';
import Footer from './components/Footer';
import AddressModal from './components/AddressModal';
import ShopSelectionModal from './components/ShopSelectionModal';

interface Product {
  id: number;
  name: string;
  price: number;
  category: string;
  topCategory: string;
  image: string;
  rating: number;
  reviews: number;
  inStock: boolean;
  discount: number;
  brand?: string;
  weight?: string;
}

interface CartItem {
  id: string | number;
  name: string;
  price: number;
  category: string;
  topCategory: string;
  image: string;
  rating: number;
  reviews: number;
  inStock: boolean;
  discount: number;
  brand?: string;
  weight?: string;
  quantity: number;
}

interface MultiShopCartItem {
  product: ShopProduct;
  shop: Shop;
  quantity: number;
}

// Language and currency translations
export const translations = {
  en: {
    // Header
    welcome: 'Welcome to Quickko',
    searchPlaceholder: 'Search products, brands...',
    cart: 'Cart',
    wishlist: 'Wishlist',
    notifications: 'Notifications',
    profile: 'Profile',
    orders: 'My Orders',
    deliverTo: 'Deliver to',
    welcomeBack: 'Welcome back,',
    personalInformation: 'Personal Information',
    logout: 'Logout',
    login: 'Login',
    mainStore: 'Dashboard',
    exploreShops: 'Explore Shops',
    browseAll: 'Browse All',
    discoverShops: 'Discover Shops',
    browseAllShops: 'Browse All Shops',
    browseAllProducts: 'Browse All Products',
    cancel: 'Cancel',
    search: 'Search',
    popularProducts: 'Popular Products',
    shopsNearYou: 'Shops Near You',
    seeAll: 'See All',
    add: 'Add',
    selectDeliveryAddress: 'Select Delivery Address',
    home: 'Home',
    office: 'Office',
    newMember: 'New Member',
    loadingProducts: 'Loading products...',
    loadingShops: 'Loading shops...',
    loadingCategories: 'Loading categories...',
    food: 'Food',
    fresh: 'Fresh',
    mart: 'Mart',
    health: 'Health',
    findShops: 'Find Shops',
    loyalty: 'Loyalty',
    
    // Categories
    shopByMainCategory: 'Shop by Main Category',
    selectCategories: 'Select one or more categories to filter products',
    shopBySubcategory: 'Shop by Subcategory',
    current: 'Current',
    
    // Main Categories
    groceries: 'Groceries',
    household: 'Household',
    personalCare: 'Personal Care',
    beverages: 'Beverages',
    electronics: 'Electronics',
    clothing: 'Clothing',
    
    // Sub Categories
    allProducts: 'All Products',
    grainsFlours: 'Grains & Flours',
    lentilsPulses: 'Lentils & Pulses',
    spicesBlends: 'Spices & Blends',
    oilsGhee: 'Oils & Ghee',
    dairyFresh: 'Dairy & Fresh',
    picklesChutneys: 'Pickles & Chutneys',
    snacksSweets: 'Snacks & Sweets',
    nutsSeeds: 'Nuts & Seeds',
    readyMixes: 'Ready Mixes',
    frozenRte: 'Frozen & RTE',
    
    // Product Category Names
    bakeries: 'Bakeries',
    sweetShops: 'Sweet Shops',
    homeFood: 'Home Food',
    pickles: 'Pickles',
    dairy: 'Dairy',
    fruitsVeg: 'Fruits & Vegetables',
    flowers: 'Flowers',
    meat: 'Meat',
    kirana: 'Kirana',
    organic: 'Organic',
    riceGrains: 'Rice & Grains',
    dryFruits: 'Dry Fruits',
    pharmacy: 'Pharmacy',
    ayurvedic: 'Ayurvedic',
    dentalCare: 'Dental Care',
    wellness: 'Wellness',
    restaurant: 'Restaurant',
    specialty: 'Specialty',
    
    // Shop-related translations
    open: 'Open',
    closed: 'Closed',
    temporarilyClosed: 'Temporarily Closed',
    visitShop: 'Visit Shop',
    viewDetails: 'View Details',
    shopInfo: 'Shop Info',
    browseProducts: 'Browse Products',
    shopUnavailable: 'Shop Unavailable',
    allShops: 'All Shops',
    filtered: 'Filtered',
    active: 'Active',
    viewAllShops: 'View All Shops',
    showingShops: 'Showing {count} {type} shop{plural}',
    findNearbyShops: 'Find your nearby and specialty shops with simple filters',
    shopStatus: 'Shop Status',
    shopCurrentlyOpen: 'This shop is currently open and accepting orders',
    shopCurrentlyClosed: 'This shop is currently closed or unavailable',
    description: 'Description',
    location: 'Location',
    contactInformation: 'Contact Information',
    phone: 'Phone',
    email: 'Email',
    rating: 'Rating',
    reviews: 'reviews',
    shopTheme: 'Shop Theme',
    shopNotFound: 'Shop Not Found',
    shopNotFoundMessage: 'The shop you\'re looking for doesn\'t exist or has been removed.',
    goBackHome: 'Go Back Home',
    loadingShopDetails: 'Loading shop details...',
    discoveringShops: 'Discovering shops...',
    searchAndFilters: 'Search & Filters',
    searchShopsPlaceholder: 'Search shops by name, description, or location...',
    shops: 'Shops',
    back: 'Back',
    
    // Shop type translations
    groceryStore: 'Grocery Store',
    bakery: 'Bakery',
    medical: 'Medical',
    specialtyStore: 'Specialty Store',
    generalStore: 'General Store',
    
    // Product actions
    addToCart: 'Add to Cart',
    addToWishlist: 'Add to Wishlist',
    removeFromWishlist: 'Remove from Wishlist',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    products: 'Products',
    
    // Features
    features: 'Features',
    searchProducts: 'Search Products',
    cartManagement: 'Cart Management',
    productRatings: 'Product Ratings',
    discounts: 'Discounts',
    stockStatus: 'Stock Status',
    userStats: 'User Statistics',
    loyaltyProgram: 'Loyalty Program',
    
    // Why Choose Section
    whyChoose: 'Why Choose Quickko?',
    whyChooseSubtitle: 'Discover the advantages that make us your preferred choice',
    authenticProducts: 'Authentic Products',
    authenticProductsDesc: 'Genuine Indian brands and products you can trust',
    wideSelection: 'Wide Selection',
    wideSelectionDesc: 'Comprehensive range of groceries, household items, and more',
    competitivePricing: 'Competitive Pricing',
    competitivePricingDesc: 'Best prices with regular discounts and offers',
    fastDelivery: 'Fast Delivery',
    fastDeliveryDesc: 'Quick and reliable delivery to your doorstep',
    customerSupport: 'Customer Support',
    customerSupportDesc: '24/7 support for all your queries and concerns',
    loyaltyRewards: 'Loyalty Rewards',
    loyaltyRewardsDesc: 'Earn points and get rewards on every purchase',
    
    // Product Names
    // Grains & Flours
    basmatiRice: 'Basmati Rice',
    sonaMooriRice: 'Sona Moori Rice',
    wheatFlour: 'Wheat Flour',
    ragiFlour: 'Ragi Flour',
    jowarFlour: 'Jowar Flour',
    
    // Lentils & Pulses
    toorDal: 'Toor Dal',
    moongDal: 'Moong Dal',
    uradDal: 'Urad Dal',
    chanaDal: 'Chana Dal',
    
    // Spices & Blends
    garamMasala: 'Garam Masala',
    turmericPowder: 'Turmeric Powder',
    redChilliPowder: 'Red Chilli Powder',
    cuminSeeds: 'Cumin Seeds',
    corianderPowder: 'Coriander Powder',
    blackPepper: 'Black Pepper',
    
    // Oils & Ghee
    amulGhee: 'Amul Pure Ghee',
    aashirvaadGhee: 'Aashirvaad Ghee',
    mustardOil: 'Mustard Oil',
    jaggery: 'Jaggery (Block)',
    
    // Dairy & Fresh
    amulPaneer: 'Amul Paneer',
    freshGinger: 'Fresh Ginger',
    curryLeaves: 'Fresh Curry Leaves',
    yogurt: 'Yogurt / Dahi',
    
    // Pickles & Chutneys
    mangoPickle: 'Mother\'s Recipe Mango Pickle',
    limePickle: 'Priya Lime Pickle',
    mixedPickle: 'Pachranga Mixed Pickle',
    gingerPaste: 'MTR Ginger Paste',
    
    // Snacks & Sweets
    namakPare: 'Haldiram\'s Namak Pare',
    gulabJamunMix: 'Haldiram\'s Gulab Jamun Mix',
    mixture: 'Bikaji Mixture',
    sev: 'Balaji Sev',
    
    // Beverages
    tataTea: 'Tata Tea Premium',
    filterCoffee: 'Filter Coffee Powder',
    roohAfza: 'Rooh Afza Syrup',
    maggiNoodles: 'Maggi Masala Noodles',
    
    // Nuts & Seeds
    almonds: 'Premium Almonds',
    cashews: 'Cashews (Whole)',
    raisins: 'Raisins (Kishmish)',
    flaxSeeds: 'Flax Seeds',
    
    // Ready Mixes
    idliMix: 'Gits Idli Mix',
    dosaMix: 'MTR Dosa Mix',
    upmaMix: 'Tasty Bite Upma Mix',
    dhoklaMix: 'Aashirvaad Dhokla Mix',
    
    // Frozen & RTE
    palakPaneer: 'Deep Indian Kitchen Palak Paneer',
    minuteKhana: 'Haldiram\'s Minute Khana',
    frozenParatha: 'Frozen Paratha (Pack of 6)',
    frozenSamosa: 'Frozen Samosa Mix',
    
    // Household
    dishSoap: 'Dish Soap',
    paperTowels: 'Paper Towels',
    laundryDetergent: 'Laundry Detergent',
    allPurposeCleaner: 'All-Purpose Cleaner',
    
    // Personal Care
    toothpaste: 'Toothpaste',
    shampoo: 'Shampoo',
    bodyWash: 'Body Wash',
    deodorant: 'Deodorant',
    
    // Electronics
    smartphone: 'Smartphone',
    laptop: 'Laptop',
    headphones: 'Headphones',
    powerBank: 'Power Bank',
    
    // Clothing
    tShirt: 'T-Shirt',
    jeans: 'Jeans',
    sneakers: 'Sneakers',
    jacket: 'Jacket',
    
    // Footer
    footer: '© 2024 Quickko. All rights reserved.'
  },
  te: {
    // Header
    welcome: 'హోమ్ నీడ్స్ రీఫిల్‌కి స్వాగతం',
    searchPlaceholder: 'ఉత్పత్తులు, బ్రాండ్లు వెతకండి...',
    cart: 'కార్ట్',
    wishlist: 'ఇష్టమైనవి',
    notifications: 'నోటిఫికేషన్లు',
    profile: 'ప్రొఫైల్',
    orders: 'నా ఆర్డర్లు',
    deliverTo: 'డెలివరీ చేయండి',
    welcomeBack: 'మళ్లీ స్వాగతం,',
    personalInformation: 'వ్యక్తిగత సమాచారం',
    logout: 'లాగ్అవుట్',
    login: 'లాగిన్',
    mainStore: 'ప్రధాన స్టోర్',
    exploreShops: 'షాపులను అన్వేషించండి',
    browseAll: 'అన్నింటినీ బ్రౌజ్ చేయండి',
    discoverShops: 'షాపులను కనుగొనండి',
    browseAllShops: 'అన్ని షాపులను బ్రౌజ్ చేయండి',
    browseAllProducts: 'అన్ని ఉత్పత్తులను బ్రౌజ్ చేయండి',
    cancel: 'రద్దు చేయండి',
    search: 'వెతకండి',
    popularProducts: 'జనాదరణ పొందిన ఉత్పత్తులు',
    shopsNearYou: 'మీకు సమీపంలో ఉన్న షాపులు',
    seeAll: 'అన్నీ చూడండి',
    add: 'జోడించు',
    selectDeliveryAddress: 'డెలివరీ చిరునామాను ఎంచుకోండి',
    home: 'ఇల్లు',
    office: 'ఆఫీస్',
    newMember: 'కొత్త సభ్యుడు',
    loadingProducts: 'ఉత్పత్తులు లోడ్ అవుతున్నాయి...',
    loadingShops: 'షాపులు లోడ్ అవుతున్నాయి...',
    loadingCategories: 'వర్గాలు లోడ్ అవుతున్నాయి...',
    food: 'ఆహారం',
    fresh: 'తాజా',
    mart: 'మార్ట్',
    health: 'ఆరోగ్యం',
    findShops: 'షాపులను కనుగొనండి',
    loyalty: 'విశ్వసనీయత',
    
    // Categories
    shopByMainCategory: 'ప్రధాన వర్గాల వారీగా షాప్ చేయండి',
    selectCategories: 'ఉత్పత్తులను ఫిల్టర్ చేయడానికి ఒకటి లేదా అంతకంటే ఎక్కువ వర్గాలను ఎంచుకోండి',
    shopBySubcategory: 'ఉప-వర్గాల వారీగా షాప్ చేయండి',
    current: 'ప్రస్తుతం',
    
    // Main Categories
    groceries: 'కిరాణా',
    household: 'గృహోపయోగ వస్తువులు',
    personalCare: 'వ్యక్తిగత సంరక్షణ',
    beverages: 'పానీయాలు',
    electronics: 'ఎలక్ట్రానిక్స్',
    clothing: 'దుస్తులు',
    
    // Sub Categories
    allProducts: 'అన్ని ఉత్పత్తులు',
    grainsFlours: 'ధాన్యాలు & పిండి',
    lentilsPulses: 'పప్పులు & బియ్యం',
    spicesBlends: 'మసాలాలు & మిశ్రమాలు',
    oilsGhee: 'నూనెలు & నెయ్యి',
    dairyFresh: 'పాల ఉత్పత్తులు & తాజా',
    picklesChutneys: 'ఊరగాయలు & చట్నీలు',
    snacksSweets: 'చిరుతలు & తీపి వస్తువులు',
    nutsSeeds: 'గింజలు & విత్తనాలు',
    readyMixes: 'సిద్ధమైన మిశ్రమాలు',
    frozenRte: 'ఘనీభవించిన & తినడానికి సిద్ధం',
    
    // Product Category Names
    bakeries: 'బేకరీలు',
    sweetShops: 'మిఠాయి దుకాణాలు',
    homeFood: 'హోమ్ ఫుడ్',
    pickles: 'ఊరగాయలు',
    dairy: 'పాల ఉత్పత్తులు',
    fruitsVeg: 'పళ్లు & కూరగాయలు',
    flowers: 'పువ్వులు',
    meat: 'మాంసం',
    kirana: 'కిరాణా',
    organic: 'జైవిక',
    riceGrains: 'బియ్యం & ధాన్యాలు',
    dryFruits: 'ఎండిన పళ్లు',
    pharmacy: 'ఫార్మసీ',
    ayurvedic: 'ఆయుర్వేద',
    dentalCare: 'దంత సంరక్షణ',
    wellness: 'ఆరోగ్యం',
    restaurant: 'రెస్టారెంట్',
    specialty: 'ప్రత్యేక',
    
    // Shop-related translations
    open: 'తెరిచి',
    closed: 'మూసివేయబడింది',
    temporarilyClosed: 'తాత్కాలికంగా మూసివేయబడింది',
    visitShop: 'దుకాణాన్ని సందర్శించండి',
    viewDetails: 'వివరాలను వీక్షించండి',
    shopInfo: 'దుకాణ సమాచారం',
    browseProducts: 'ఉత్పత్తులను బ్రౌజ్ చేయండి',
    shopUnavailable: 'దుకాణం అందుబాటులో లేదు',
    allShops: 'అన్ని దుకాణాలు',
    filtered: 'ఫిల్టర్ చేయబడింది',
    active: 'సక్రియ',
    viewAllShops: 'అన్ని దుకాణాలను వీక్షించండి',
    showingShops: '{count} {type} దుకాణ{plural} చూపిస్తోంది',
    findNearbyShops: 'సాధారణ ఫిల్టర్లతో మీ సమీపంలోని మరియు ప్రత్యేక దుకాణాలను కనుగొనండి',
    shopStatus: 'దుకాణ స్థితి',
    shopCurrentlyOpen: 'ఈ దుకాణం ప్రస్తుతం తెరిచి ఉంది మరియు ఆర్డర్లను అంగీకరిస్తోంది',
    shopCurrentlyClosed: 'ఈ దుకాణం ప్రస్తుతం మూసివేయబడింది లేదా అందుబాటులో లేదు',
    description: 'వివరణ',
    location: 'స్థానం',
    contactInformation: 'సంప్రదింపు సమాచారం',
    phone: 'ఫోన్',
    email: 'ఇమెయిల్',
    rating: 'రేటింగ్',
    reviews: 'సమీక్షలు',
    shopTheme: 'దుకాణ థీమ్',
    shopNotFound: 'దుకాణం కనుగొనబడలేదు',
    shopNotFoundMessage: 'మీరు వెతుకుతున్న దుకాణం ఉనికిలో లేదు లేదా తీసివేయబడింది.',
    goBackHome: 'హోమ్‌కు తిరిగి వెళ్ళండి',
    loadingShopDetails: 'దుకాణ వివరాలను లోడ్ చేస్తోంది...',
    discoveringShops: 'దుకాణాలను కనుగొనడం...',
    searchAndFilters: 'శోధన & ఫిల్టర్లు',
    searchShopsPlaceholder: 'పేరు, వివరణ లేదా స్థానం ద్వారా దుకాణాలను శోధించండి...',
    shops: 'దుకాణాలు',
    back: 'వెనక్కి',
    groceryStore: 'కిరాణా దుకాణం',
    bakery: 'బేకరీ',
    medical: 'వైద్య',
    specialtyStore: 'ప్రత్యేక దుకాణం',
    generalStore: 'సాధారణ దుకాణం',
    
    // Product actions
    addToCart: 'కార్ట్‌లో జోడించండి',
    addToWishlist: 'ఇష్టమైనవాటిలో జోడించండి',
    removeFromWishlist: 'ఇష్టమైనవాటి నుండి తీసివేయండి',
    inStock: 'స్టాక్‌లో ఉంది',
    outOfStock: 'స్టాక్‌లో లేదు',
    products: 'ఉత్పత్తులు',
    
    // Features
    features: 'లక్షణాలు',
    searchProducts: 'ఉత్పత్తులు వెతకండి',
    cartManagement: 'కార్ట్ నిర్వహణ',
    productRatings: 'ఉత్పత్తి రేటింగ్లు',
    discounts: 'డిస్కౌంట్లు',
    stockStatus: 'స్టాక్ స్థితి',
    userStats: 'వినియోగదారు గణాంకాలు',
    loyaltyProgram: 'విశ్వాసార్హత కార్యక్రమం',
    
    // Why Choose Section
    whyChoose: 'ఎందుకు హోమ్ నీడ్స్ రీఫిల్‌ని ఎంచుకోవాలి?',
    whyChooseSubtitle: 'మమ్మల్ని మీ ప్రాధాన్య ఎంపికగా చేసే ప్రయోజనాలను కనుగొనండి',
    authenticProducts: 'అధికారిక ఉత్పత్తులు',
    authenticProductsDesc: 'మీరు విశ్వసించగల నిజమైన భారతీయ బ్రాండ్లు మరియు ఉత్పత్తులు',
    wideSelection: 'విస్తృత ఎంపిక',
    wideSelectionDesc: 'కిరాణా, గృహోపయోగ వస్తువులు మరియు మరిన్ని యొక్క సమగ్ర శ్రేణి',
    competitivePricing: 'పోటీ ధరలు',
    competitivePricingDesc: 'నిరంతర డిస్కౌంట్లు మరియు ఆఫర్లతో ఉత్తమ ధరలు',
    fastDelivery: 'వేగవంతమైన డెలివరీ',
    fastDeliveryDesc: 'మీ ఇంటి వద్దకు వేగవంతమైన మరియు నమ్మదగిన డెలివరీ',
    customerSupport: 'వినియోగదారు మద్దతు',
    customerSupportDesc: 'మీ అన్ని ప్రశ్నలు మరియు ఆందోళనలకు 24/7 మద్దతు',
    loyaltyRewards: 'విశ్వాసార్హత బహుమతులు',
    loyaltyRewardsDesc: 'ప్రతి కొనుగోలుపై పాయింట్లు సంపాదించండి మరియు బహుమతులు పొందండి',
    
    // Product Names
    // Grains & Flours
    basmatiRice: 'బాస్మతి బియ్యం',
    sonaMooriRice: 'సోనా మూరి బియ్యం',
    wheatFlour: 'గోధుమ పిండి',
    ragiFlour: 'రాగి పిండి',
    jowarFlour: 'జొన్న పిండి',
    
    // Lentils & Pulses
    toorDal: 'తూర్ పప్పు',
    moongDal: 'మూంగ్ పప్పు',
    uradDal: 'ఉరద్ పప్పు',
    chanaDal: 'చనా పప్పు',
    
    // Spices & Blends
    garamMasala: 'గరం మసాలా',
    turmericPowder: 'పసుపు పొడి',
    redChilliPowder: 'ఎరుపు మిరపపొడి',
    cuminSeeds: 'జీలకర్ర',
    corianderPowder: 'ధనియా పొడి',
    blackPepper: 'నల్ల మిరియాలు',
    
    // Oils & Ghee
    amulGhee: 'అమూల్ ఘి',
    aashirvaadGhee: 'ఆశీర్వాద్ ఘి',
    mustardOil: 'ఆవ నూనె',
    jaggery: 'బెల్లం',
    
    // Dairy & Fresh
    amulPaneer: 'అమూల్ పనీర్',
    freshGinger: 'తాజా అల్లం',
    curryLeaves: 'కరివేపాకు',
    yogurt: 'మజ్జిగ',
    
    // Pickles & Chutneys
    mangoPickle: 'మామిడి ఊరగాయ',
    limePickle: 'నిమ్మ ఊరగాయ',
    mixedPickle: 'మిశ్రమ ఊరగాయ',
    gingerPaste: 'అల్లం పేస్ట్',
    
    // Snacks & Sweets
    namakPare: 'నమక్ పరే',
    gulabJamunMix: 'గులాబ్ జామున్ మిక్స్',
    mixture: 'మిశ్రమం',
    sev: 'సేవ్',
    
    // Beverages
    tataTea: 'టాటా టీ',
    filterCoffee: 'ఫిల్టర్ కాఫీ',
    roohAfza: 'రూహ్ అఫ్జా',
    maggiNoodles: 'మాగీ నూడుల్స్',
    
    // Nuts & Seeds
    almonds: 'బాదం',
    cashews: 'జీడి పప్పు',
    raisins: 'ఉన్నేలు',
    flaxSeeds: 'అవిసె విత్తనాలు',
    
    // Ready Mixes
    idliMix: 'ఇడ్లి మిక్స్',
    dosaMix: 'దోస మిక్స్',
    upmaMix: 'ఉప్మా మిక్స్',
    dhoklaMix: 'ధోక్లా మిక్స్',
    
    // Frozen & RTE
    palakPaneer: 'పాలక్ పనీర్',
    minuteKhana: 'మినిట్ ఖానా',
    frozenParatha: 'ఫ్రోజెన్ పరాటా',
    frozenSamosa: 'ఫ్రోజెన్ సమోసా',
    
    // Household
    dishSoap: 'పాత్రల సబ్బు',
    paperTowels: 'కాగితపు తువ్వాలు',
    laundryDetergent: 'టోపీ డిటర్జెంట్',
    allPurposeCleaner: 'అన్ని ప్రయోజనాల క్లీనర్',
    
    // Personal Care
    toothpaste: 'టూత్ పేస్ట్',
    shampoo: 'షాంపూ',
    bodyWash: 'బాడీ వాష్',
    deodorant: 'డియోడరంట్',
    
    // Electronics
    smartphone: 'స్మార్ట్ ఫోన్',
    laptop: 'ల్యాప్టాప్',
    headphones: 'హెడ్ ఫోన్స్',
    powerBank: 'పవర్ బ్యాంక్',
    
    // Clothing
    tShirt: 'టీ షర్ట్',
    jeans: 'జీన్స్',
    sneakers: 'స్నీకర్స్',
    jacket: 'జాకెట్',
    
    // Footer
    footer: '© 2024 హోమ్ నీడ్స్ రీఫిల్. అన్ని హక్కులు కాపాడబడ్డాయి.'
  },
  hi: {
    // Header
    welcome: 'होम नीड्स रीफिल में आपका स्वागत है',
    searchPlaceholder: 'उत्पाद, ब्रांड खोजें...',
    cart: 'कार्ट',
    wishlist: 'इच्छा सूची',
    notifications: 'सूचनाएं',
    profile: 'प्रोफाइल',
    orders: 'मेरे ऑर्डर',
    deliverTo: 'डिलीवर करें',
    welcomeBack: 'वापसी पर स्वागत है,',
    personalInformation: 'व्यक्तिगत जानकारी',
    logout: 'लॉगआउट',
    login: 'लॉगिन',
    mainStore: 'मुख्य स्टोर',
    exploreShops: 'दुकानें खोजें',
    browseAll: 'सभी ब्राउज़ करें',
    discoverShops: 'दुकानें खोजें',
    browseAllShops: 'सभी दुकानें ब्राउज़ करें',
    browseAllProducts: 'सभी उत्पाद ब्राउज़ करें',
    cancel: 'रद्द करें',
    search: 'खोजें',
    popularProducts: 'लोकप्रिय उत्पाद',
    shopsNearYou: 'आपके पास की दुकानें',
    seeAll: 'सभी देखें',
    add: 'जोड़ें',
    selectDeliveryAddress: 'डिलीवरी पता चुनें',
    home: 'घर',
    office: 'कार्यालय',
    newMember: 'नया सदस्य',
    loadingProducts: 'उत्पाद लोड हो रहे हैं...',
    loadingShops: 'दुकानें लोड हो रही हैं...',
    loadingCategories: 'श्रेणियां लोड हो रही हैं...',
    food: 'खाना',
    fresh: 'ताजा',
    mart: 'मार्ट',
    health: 'स्वास्थ्य',
    findShops: 'दुकानें खोजें',
    loyalty: 'वफादारी',
    
    // Categories
    shopByMainCategory: 'मुख्य श्रेणियों के अनुसार खरीदारी करें',
    selectCategories: 'उत्पादों को फ़िल्टर करने के लिए एक या अधिक श्रेणियां चुनें',
    shopBySubcategory: 'उप-श्रेणियों के अनुसार खरीदारी करें',
    current: 'वर्तमान',
    
    // Main Categories
    groceries: 'किराना',
    household: 'घरेलू सामान',
    personalCare: 'व्यक्तिगत देखभाल',
    beverages: 'पेय पदार्थ',
    electronics: 'इलेक्ट्रॉनिक्स',
    clothing: 'कपड़े',
    
    // Sub Categories
    allProducts: 'सभी उत्पाद',
    grainsFlours: 'अनाज और आटा',
    lentilsPulses: 'दालें और फलियां',
    spicesBlends: 'मसाले और मिश्रण',
    oilsGhee: 'तेल और घी',
    dairyFresh: 'दूध उत्पाद और ताजा',
    picklesChutneys: 'अचार और चटनी',
    snacksSweets: 'नाश्ता और मिठाई',
    nutsSeeds: 'मेवे और बीज',
    readyMixes: 'तैयार मिश्रण',
    frozenRte: 'फ्रोजन और तैयार भोजन',
    
    // Product Category Names
    bakeries: 'बेकरी',
    sweetShops: 'मिठाई की दुकानें',
    homeFood: 'घरेलू खाना',
    pickles: 'अचार',
    dairy: 'डेयरी',
    fruitsVeg: 'फल और सब्जियां',
    flowers: 'फूल',
    meat: 'मांस',
    kirana: 'किराना',
    organic: 'जैविक',
    riceGrains: 'चावल और अनाज',
    dryFruits: 'सूखे मेवे',
    pharmacy: 'फार्मेसी',
    ayurvedic: 'आयुर्वेदिक',
    dentalCare: 'दंत देखभाल',
    wellness: 'कल्याण',
    restaurant: 'रेस्तरां',
    specialty: 'विशेष',
    
    // Shop-related translations
    open: 'खुला',
    closed: 'बंद',
    temporarilyClosed: 'अस्थायी रूप से बंद',
    visitShop: 'दुकान पर जाएं',
    viewDetails: 'विवरण देखें',
    shopInfo: 'दुकान की जानकारी',
    browseProducts: 'उत्पाद ब्राउज़ करें',
    shopUnavailable: 'दुकान उपलब्ध नहीं है',
    allShops: 'सभी दुकानें',
    filtered: 'फ़िल्टर किया गया',
    active: 'सक्रिय',
    viewAllShops: 'सभी दुकानें देखें',
    showingShops: '{count} {type} दुकान{plural} दिखा रहा है',
    findNearbyShops: 'सरल फ़िल्टर के साथ अपने पास की और विशेष दुकानें खोजें',
    shopStatus: 'दुकान की स्थिति',
    shopCurrentlyOpen: 'यह दुकान वर्तमान में खुली है और ऑर्डर स्वीकार कर रही है',
    shopCurrentlyClosed: 'यह दुकान वर्तमान में बंद है या उपलब्ध नहीं है',
    description: 'विवरण',
    location: 'स्थान',
    contactInformation: 'संपर्क जानकारी',
    phone: 'फ़ोन',
    email: 'ईमेल',
    rating: 'रेटिंग',
    reviews: 'समीक्षाएं',
    shopTheme: 'दुकान थीम',
    shopNotFound: 'दुकान नहीं मिली',
    shopNotFoundMessage: 'आप जिस दुकान की तलाश कर रहे हैं वह मौजूद नहीं है या हटा दी गई है।',
    goBackHome: 'होम पर वापस जाएं',
    loadingShopDetails: 'दुकान विवरण लोड हो रहे हैं...',
    discoveringShops: 'दुकानें खोजी जा रही हैं...',
    searchAndFilters: 'खोज और फ़िल्टर',
    searchShopsPlaceholder: 'नाम, विवरण या स्थान से दुकानें खोजें...',
    shops: 'दुकानें',
    back: 'वापस',
    groceryStore: 'किराना दुकान',
    bakery: 'बेकरी',
    medical: 'चिकित्सा',
    specialtyStore: 'विशेष दुकान',
    generalStore: 'सामान्य दुकान',
    
    // Product actions
    addToCart: 'कार्ट में जोड़ें',
    addToWishlist: 'इच्छा सूची में जोड़ें',
    removeFromWishlist: 'इच्छा सूची से हटाएं',
    inStock: 'स्टॉक में उपलब्ध',
    outOfStock: 'स्टॉक में नहीं',
    products: 'उत्पाद',
    
    // Features
    features: 'सुविधाएं',
    searchProducts: 'उत्पाद खोजें',
    cartManagement: 'कार्ट प्रबंधन',
    productRatings: 'उत्पाद रेटिंग',
    discounts: 'छूट',
    stockStatus: 'स्टॉक स्थिति',
    userStats: 'उपयोगकर्ता आंकड़े',
    loyaltyProgram: 'वफादारी कार्यक्रम',
    
    // Why Choose Section
    whyChoose: 'होम नीड्स रीफिल को क्यों चुनें?',
    whyChooseSubtitle: 'उन लाभों को खोजें जो हमें आपकी पसंदीदा पसंद बनाते हैं',
    authenticProducts: 'प्रामाणिक उत्पाद',
    authenticProductsDesc: 'विश्वसनीय भारतीय ब्रांड और उत्पाद',
    wideSelection: 'व्यापक चयन',
    wideSelectionDesc: 'किराना, घरेलू सामान और अधिक की व्यापक श्रृंखला',
    competitivePricing: 'प्रतिस्पर्धी मूल्य',
    competitivePricingDesc: 'नियमित छूट और ऑफर के साथ सर्वोत्तम मूल्य',
    fastDelivery: 'तेज डिलीवरी',
    fastDeliveryDesc: 'आपके दरवाजे तक तेज और विश्वसनीय डिलीवरी',
    customerSupport: 'ग्राहक सहायता',
    customerSupportDesc: 'आपके सभी प्रश्नों और चिंताओं के लिए 24/7 सहायता',
    loyaltyRewards: 'वफादारी पुरस्कार',
    loyaltyRewardsDesc: 'हर खरीदारी पर अंक अर्जित करें और पुरस्कार प्राप्त करें',
    
    // Product Names
    // Grains & Flours
    basmatiRice: 'बासमती चावल',
    sonaMooriRice: 'सोना मूरी चावल',
    wheatFlour: 'गेहूं का आटा',
    ragiFlour: 'रागी का आटा',
    jowarFlour: 'ज्वार का आटा',
    
    // Lentils & Pulses
    toorDal: 'तूर दाल',
    moongDal: 'मूंग दाल',
    uradDal: 'उड़द दाल',
    chanaDal: 'चना दाल',
    
    // Spices & Blends
    garamMasala: 'गरम मसाला',
    turmericPowder: 'हल्दी पाउडर',
    redChilliPowder: 'लाल मिर्च पाउडर',
    cuminSeeds: 'जीरा',
    corianderPowder: 'धनिया पाउडर',
    blackPepper: 'काली मिर्च',
    
    // Oils & Ghee
    amulGhee: 'अमूल घी',
    aashirvaadGhee: 'आशीर्वाद घी',
    mustardOil: 'सरसों का तेल',
    jaggery: 'गुड़',
    
    // Dairy & Fresh
    amulPaneer: 'अमूल पनीर',
    freshGinger: 'ताजा अदरक',
    curryLeaves: 'करी पत्ता',
    yogurt: 'दही',
    
    // Pickles & Chutneys
    mangoPickle: 'आम का अचार',
    limePickle: 'नींबू का अचार',
    mixedPickle: 'मिश्रित अचार',
    gingerPaste: 'अदरक का पेस्ट',
    
    // Snacks & Sweets
    namakPare: 'नमक पारे',
    gulabJamunMix: 'गुलाब जामुन मिक्स',
    mixture: 'मिश्रण',
    sev: 'सेव',
    
    // Beverages
    tataTea: 'टाटा टी',
    filterCoffee: 'फिल्टर कॉफी',
    roohAfza: 'रूह अफजा',
    maggiNoodles: 'मैगी मसाला नूडल्स',
    
    // Nuts & Seeds
    almonds: 'बादाम',
    cashews: 'काजू',
    raisins: 'किशमिश',
    flaxSeeds: 'अलसी के बीज',
    
    // Ready Mixes
    idliMix: 'इडली मिक्स',
    dosaMix: 'डोसा मिक्स',
    upmaMix: 'उपमा मिक्स',
    dhoklaMix: 'धोकला मिक्स',
    
    // Frozen & RTE
    palakPaneer: 'पालक पनीर',
    minuteKhana: 'मिनट खाना',
    frozenParatha: 'फ्रोजन पराठा',
    frozenSamosa: 'फ्रोजन समोसा',
    
    // Household
    dishSoap: 'बर्तन साबुन',
    paperTowels: 'कागज के तौलिए',
    laundryDetergent: 'कपड़े धोने का साबुन',
    allPurposeCleaner: 'सर्व-उद्देश्य क्लीनर',
    
    // Personal Care
    toothpaste: 'टूथपेस्ट',
    shampoo: 'शैम्पू',
    bodyWash: 'बॉडी वॉश',
    deodorant: 'डिओडरेंट',
    
    // Electronics
    smartphone: 'स्मार्टफोन',
    laptop: 'लैपटॉप',
    headphones: 'हेडफोन्स',
    powerBank: 'पावर बैंक',
    
    // Clothing
    tShirt: 'टी-शर्ट',
    jeans: 'जींस',
    sneakers: 'स्नीकर्स',
    jacket: 'जैकेट',
    
    // Footer
    footer: '© 2024 होम नीड्स रीफिल। सर्वाधिकार सुरक्षित।'
  },
  ta: {
    // Header
    welcome: 'ஹோம் நீட்ஸ் ரீஃபில்‌க்கு வரவேற்கிறோம்',
    searchPlaceholder: 'தயாரிப்புகள், பிராண்டுகளைத் தேடுங்கள்...',
    cart: 'கார்ட்',
    wishlist: 'விருப்பப்பட்டியல்',
    notifications: 'அறிவிப்புகள்',
    profile: 'சுயவிவரம்',
    orders: 'எனது ஆர்டர்கள்',
    deliverTo: 'வழங்க',
    welcomeBack: 'மீண்டும் வரவேற்கிறோம்,',
    personalInformation: 'தனிப்பட்ட தகவல்',
    logout: 'வெளியேற',
    login: 'உள்நுழை',
    mainStore: 'முதன்மை கடை',
    exploreShops: 'கடைகளை ஆராயுங்கள்',
    browseAll: 'அனைத்தையும் உலாவுங்கள்',
    discoverShops: 'கடைகளைக் கண்டறியுங்கள்',
    browseAllShops: 'அனைத்து கடைகளையும் உலாவுங்கள்',
    browseAllProducts: 'அனைத்து தயாரிப்புகளையும் உலாவுங்கள்',
    cancel: 'ரத்துசெய்',
    search: 'தேடு',
    popularProducts: 'பிரபலமான தயாரிப்புகள்',
    shopsNearYou: 'உங்களுக்கு அருகிலுள்ள கடைகள்',
    seeAll: 'அனைத்தையும் பார்க்க',
    add: 'சேர்',
    selectDeliveryAddress: 'வழங்கல் முகவரியைத் தேர்ந்தெடுக்கவும்',
    home: 'வீடு',
    office: 'அலுவலகம்',
    newMember: 'புதிய உறுப்பினர்',
    loadingProducts: 'தயாரிப்புகள் ஏற்றப்படுகின்றன...',
    loadingShops: 'கடைகள் ஏற்றப்படுகின்றன...',
    loadingCategories: 'வகைகள் ஏற்றப்படுகின்றன...',
    food: 'உணவு',
    fresh: 'புதிய',
    mart: 'மார்ட்',
    health: 'ஆரோக்கியம்',
    findShops: 'கடைகளைக் கண்டறியுங்கள்',
    loyalty: 'விசுவாசம்',
    
    // Categories
    shopByMainCategory: 'முதன்மை வகைகளின்படி கடைப்பிடிக்கவும்',
    selectCategories: 'தயாரிப்புகளை வடிகட்ட ஒன்று அல்லது அதற்கு மேற்பட்ட வகைகளைத் தேர்ந்தெடுக்கவும்',
    shopBySubcategory: 'துணை வகைகளின்படி கடைப்பிடிக்கவும்',
    current: 'தற்போதைய',
    
    // Main Categories
    groceries: 'கடை',
    household: 'வீட்டு உபயோகப் பொருட்கள்',
    personalCare: 'தனிப்பட்ட பராமரிப்பு',
    beverages: 'பானங்கள்',
    electronics: 'மின்னணுவியல்',
    clothing: 'ஆடைகள்',
    
    // Sub Categories
    allProducts: 'அனைத்து தயாரிப்புகளும்',
    grainsFlours: 'தானியங்கள் & மாவு',
    lentilsPulses: 'பருப்பு வகைகள் & பருப்பு',
    spicesBlends: 'மசாலாப் பொருட்கள் & கலவைகள்',
    oilsGhee: 'எண்ணெய்கள் & நெய்',
    dairyFresh: 'பால் பொருட்கள் & புதியவை',
    picklesChutneys: 'ஊறுகாய்கள் & சட்னிகள்',
    snacksSweets: 'சிற்றுண்டிகள் & இனிப்புகள்',
    nutsSeeds: 'கொட்டைகள் & விதைகள்',
    readyMixes: 'தயாராக உள்ள கலவைகள்',
    frozenRte: 'உறைந்த & சாப்பிட தயாராக உள்ளவை',
    
    // Product Category Names
    bakeries: 'பேக்கரிகள்',
    sweetShops: 'இனிப்பு கடைகள்',
    homeFood: 'வீட்டு உணவு',
    pickles: 'ஊறுகாய்',
    dairy: 'பால் பொருட்கள்',
    fruitsVeg: 'பழங்கள் & காய்கறிகள்',
    flowers: 'மலர்கள்',
    meat: 'இறைச்சி',
    kirana: 'கிரானா',
    organic: 'கரிம',
    riceGrains: 'அரிசி & தானியங்கள்',
    dryFruits: 'உலர்ந்த பழங்கள்',
    pharmacy: 'மருந்தகம்',
    ayurvedic: 'ஆயுர்வேத',
    dentalCare: 'பல் பராமரிப்பு',
    wellness: 'நல்வாழ்வு',
    restaurant: 'உணவகம்',
    specialty: 'சிறப்பு',
    
    // Shop-related translations
    open: 'திறந்த',
    closed: 'மூடப்பட்டது',
    temporarilyClosed: 'தற்காலிகமாக மூடப்பட்டது',
    visitShop: 'கடையைப் பார்வையிட',
    viewDetails: 'விவரங்களைக் காண்க',
    shopInfo: 'கடை தகவல்',
    browseProducts: 'தயாரிப்புகளை உலாவ',
    shopUnavailable: 'கடை கிடைக்கவில்லை',
    allShops: 'அனைத்து கடைகள்',
    filtered: 'வடிகட்டப்பட்டது',
    active: 'செயலில்',
    viewAllShops: 'அனைத்து கடைகளையும் பார்க்க',
    showingShops: '{count} {type} கடை{plural} காட்டுகிறது',
    findNearbyShops: 'எளிய வடிகட்டிகளுடன் உங்களுக்கு அருகிலுள்ள மற்றும் சிறப்பு கடைகளைக் கண்டறியவும்',
    shopStatus: 'கடை நிலை',
    shopCurrentlyOpen: 'இந்த கடை தற்போது திறந்திருக்கிறது மற்றும் ஆர்டர்களை ஏற்றுக்கொள்கிறது',
    shopCurrentlyClosed: 'இந்த கடை தற்போது மூடப்பட்டுள்ளது அல்லது கிடைக்கவில்லை',
    description: 'விளக்கம்',
    location: 'இடம்',
    contactInformation: 'தொடர்பு தகவல்',
    phone: 'தொலைபேசி',
    email: 'மின்னஞ்சல்',
    rating: 'மதிப்பீடு',
    reviews: 'மதிப்புரைகள்',
    shopTheme: 'கடை தீம்',
    shopNotFound: 'கடை கிடைக்கவில்லை',
    shopNotFoundMessage: 'நீங்கள் தேடும் கடை இல்லை அல்லது அகற்றப்பட்டது.',
    goBackHome: 'வீட்டிற்குத் திரும்பு',
    loadingShopDetails: 'கடை விவரங்களை ஏற்றுகிறது...',
    discoveringShops: 'கடைகளைக் கண்டறிகிறது...',
    searchAndFilters: 'தேடல் & வடிகட்டிகள்',
    searchShopsPlaceholder: 'பெயர், விளக்கம் அல்லது இடம் மூலம் கடைகளைத் தேடுங்கள்...',
    shops: 'கடைகள்',
    back: 'பின்',
    groceryStore: 'கிரானா கடை',
    bakery: 'பேக்கரி',
    medical: 'மருத்துவ',
    specialtyStore: 'சிறப்பு கடை',
    generalStore: 'பொது கடை',
    
    // Product actions
    addToCart: 'கார்ட்டில் சேர்க்கவும்',
    addToWishlist: 'விருப்பப்பட்டியலில் சேர்க்கவும்',
    removeFromWishlist: 'விருப்பப்பட்டியலில் இருந்து நீக்கவும்',
    inStock: 'பங்கில் உள்ளது',
    outOfStock: 'பங்கில் இல்லை',
    products: 'தயாரிப்புகள்',
    
    // Features
    features: 'அம்சங்கள்',
    searchProducts: 'தயாரிப்புகளைத் தேடுங்கள்',
    cartManagement: 'கார்ட் நிர்வாகம்',
    productRatings: 'தயாரிப்பு மதிப்பீடுகள்',
    discounts: 'தள்ளுபடிகள்',
    stockStatus: 'பங்கு நிலை',
    userStats: 'பயனர் புள்ளிவிவரங்கள்',
    loyaltyProgram: 'விசுவாச திட்டம்',
    
    // Why Choose Section
    whyChoose: 'ஏன் ஹோம் நீட்ஸ் ரீஃபில்‌ஐ தேர்வு செய்ய வேண்டும்?',
    whyChooseSubtitle: 'எங்களை உங்கள் விருப்பமான தேர்வாக ஆக்கும் நன்மைகளைக் கண்டறியவும்',
    authenticProducts: 'உண்மையான தயாரிப்புகள்',
    authenticProductsDesc: 'நீங்கள் நம்பக்கூடிய உண்மையான இந்திய பிராண்டுகள் மற்றும் தயாரிப்புகள்',
    wideSelection: 'பரந்த தேர்வு',
    wideSelectionDesc: 'கடை, வீட்டு உபயோகப் பொருட்கள் மற்றும் பலவற்றின் விரிவான வரம்பு',
    competitivePricing: 'போட்டி விலைகள்',
    competitivePricingDesc: 'வழக்கமான தள்ளுபடிகள் மற்றும் சலுகைகளுடன் சிறந்த விலைகள்',
    fastDelivery: 'விரைவு டெலிவரி',
    fastDeliveryDesc: 'உங்கள் வீட்டு வாசலுக்கு விரைவான மற்றும் நம்பகமான டெலிவரி',
    customerSupport: 'வாடிக்கையாளர் ஆதரவு',
    customerSupportDesc: 'உங்கள் அனைத்து கேள்விகள் மற்றும் கவலைகளுக்கும் 24/7 ஆதரவு',
    loyaltyRewards: 'விசுவாச வெகுமதிகள்',
    loyaltyRewardsDesc: 'ஒவ்வொரு வாங்குதலிலும் புள்ளிகள் சம்பாதித்து வெகுமதிகள் பெறுங்கள்',
    
    // Product Names
    // Grains & Flours
    basmatiRice: 'பாஸ்மதி அரிசி',
    sonaMooriRice: 'சோனா மூரி அரிசி',
    wheatFlour: 'கோதுமை மாவு',
    ragiFlour: 'ராகி மாவு',
    jowarFlour: 'சோள மாவு',
    
    // Lentils & Pulses
    toorDal: 'தூர்தால்',
    moongDal: 'மூங்குதால்',
    uradDal: 'உளுதால்',
    chanaDal: 'சனாதால்',
    
    // Spices & Blends
    garamMasala: 'கரம் மசாலா',
    turmericPowder: 'மஞ்சள் பொடி',
    redChilliPowder: 'சிவப்பு மிளகாய் பொடி',
    cuminSeeds: 'சீரகம்',
    corianderPowder: 'கொத்தமல்லி பொடி',
    blackPepper: 'கருமிளகு',
    
    // Oils & Ghee
    amulGhee: 'அமுல் நெய்',
    aashirvaadGhee: 'ஆசீர்வாத் நெய்',
    mustardOil: 'கடுகு எண்ணெய்',
    jaggery: 'வெல்லம்',
    
    // Dairy & Fresh
    amulPaneer: 'அமுல் பனீர்',
    freshGinger: 'புதிய இஞ்சி',
    curryLeaves: 'கருவேப்பிலை',
    yogurt: 'தயிர்',
    
    // Pickles & Chutneys
    mangoPickle: 'மாம்பழ ஊறுகாய்',
    limePickle: 'எலுமிச்சை ஊறுகாய்',
    mixedPickle: 'கலவை ஊறுகாய்',
    gingerPaste: 'இஞ்சி பேஸ்ட்',
    
    // Snacks & Sweets
    namakPare: 'நமக் பரே',
    gulabJamunMix: 'குலாப் ஜாமுன் மிக்ஸ்',
    mixture: 'கலவை',
    sev: 'சேவ்',
    
    // Beverages
    tataTea: 'டாடா தேயிலை',
    filterCoffee: 'வடிப்பான் காபி',
    roohAfza: 'ரூஹ் அஃப்சா',
    maggiNoodles: 'மாகி மசாலா நூடுல்ஸ்',
    
    // Nuts & Seeds
    almonds: 'பாதாம்',
    cashews: 'முந்திரி',
    raisins: 'உலர்ந்த திராட்சை',
    flaxSeeds: 'அலசி விதைகள்',
    
    // Ready Mixes
    idliMix: 'இட்லி மிக்ஸ்',
    dosaMix: 'தோசை மிக்ஸ்',
    upmaMix: 'உப்புமா மிக்ஸ்',
    dhoklaMix: 'தோக்லா மிக்ஸ்',
    
    // Frozen & RTE
    palakPaneer: 'பாலக் பனீர்',
    minuteKhana: 'நிமிட கானா',
    frozenParatha: 'உறைந்த பராத்தா',
    frozenSamosa: 'உறைந்த சமோசா',
    
    // Household
    dishSoap: 'பாத்திர சோப்பு',
    paperTowels: 'காகித துவால்கள்',
    laundryDetergent: 'துவைக்கும் சோப்பு',
    allPurposeCleaner: 'அனைத்து நோக்க கழுவி',
    
    // Personal Care
    toothpaste: 'பல் துலக்கும் பேஸ்ட்',
    shampoo: 'ஷாம்பு',
    bodyWash: 'உடல் கழுவி',
    deodorant: 'வியர்வை நீக்கி',
    
    // Electronics
    smartphone: 'ஸ்மார்ட்போன்',
    laptop: 'லேப்டாப்',
    headphones: 'தலையணி',
    powerBank: 'சக்தி வங்கி',
    
    // Clothing
    tShirt: 'டி-ஷர்ட்',
    jeans: 'ஜீன்ஸ்',
    sneakers: 'ஸ்னீக்கர்ஸ்',
    jacket: 'ஜாக்கெட்',
    
    // Footer
    footer: '© 2024 ஹோம் நீட்ஸ் ரீஃபில். அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.'
  }
};

// Currency conversion rates (to USD)
const currencyRates = {
  USD: 1,
  INR: 0.012 // 1 USD = ~83 INR
};

const currencySymbols = {
  USD: '$',
  INR: '₹'
};

// Function to get translated product names
const getTranslatedProductName = (productId: number, language: string): string => {
  const t = translations[language as keyof typeof translations];
  
  switch(productId) {
    // Grains & Flours
    case 1: return t.wheatFlour;
    case 2: return t.basmatiRice;
    case 3: return t.sonaMooriRice;
    case 4: return 'Poha (Flattened Rice)'; // Keep English for now
    case 5: return 'Sabudana (Tapioca Pearls)'; // Keep English for now
    
    // Lentils & Pulses
    case 6: return t.toorDal;
    case 7: return t.moongDal;
    case 8: return 'Rajma (Kidney Beans)'; // Keep English for now
    case 9: return t.chanaDal;
    
    // Spices & Blends
    case 10: return t.garamMasala;
    case 11: return 'Everest Chaat Masala'; // Keep English for now
    case 12: return t.turmericPowder;
    case 13: return t.redChilliPowder;
    case 14: return t.cuminSeeds;
    case 15: return 'Everest Pav Bhaji Masala'; // Keep English for now
    
    // Oils & Ghee
    case 16: return t.amulGhee;
    case 17: return t.aashirvaadGhee;
    case 18: return t.mustardOil;
    case 19: return t.jaggery;
    
    // Dairy & Fresh
    case 20: return t.amulPaneer;
    case 21: return t.freshGinger;
    case 22: return t.curryLeaves;
    case 23: return t.yogurt;
    
    // Pickles & Chutneys
    case 24: return t.mangoPickle;
    case 25: return t.limePickle;
    case 26: return t.mixedPickle;
    case 27: return t.gingerPaste;
    
    // Snacks & Sweets
    case 28: return t.namakPare;
    case 29: return t.gulabJamunMix;
    case 30: return t.mixture;
    case 31: return t.sev;
    
    // Beverages
    case 32: return t.tataTea;
    case 33: return t.filterCoffee;
    case 34: return t.roohAfza;
    case 35: return t.maggiNoodles;
    
    // Nuts & Seeds
    case 36: return t.almonds;
    case 37: return t.cashews;
    case 38: return t.raisins;
    case 39: return t.flaxSeeds;
    
    // Ready Mixes
    case 40: return t.idliMix;
    case 41: return t.dosaMix;
    case 42: return t.upmaMix;
    case 43: return t.dhoklaMix;
    
    // Frozen & RTE
    case 44: return t.palakPaneer;
    case 45: return t.minuteKhana;
    case 46: return t.frozenParatha;
    case 47: return t.frozenSamosa;
    
    // Household
    case 48: return t.dishSoap;
    case 49: return t.paperTowels;
    case 50: return t.laundryDetergent;
    case 51: return t.allPurposeCleaner;
    
    // Personal Care
    case 52: return t.toothpaste;
    case 53: return t.shampoo;
    case 54: return t.bodyWash;
    case 55: return t.deodorant;
    
    // Electronics
    case 56: return t.smartphone;
    case 57: return t.laptop;
    case 58: return t.headphones;
    case 59: return t.powerBank;
    
    // Clothing
    case 60: return t.tShirt;
    case 61: return t.jeans;
    case 62: return t.sneakers;
    case 63: return t.jacket;
    
    default: return 'Unknown Product';
  }
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, isAuthenticated } = useAuth();
  const [selectedTopCategory] = useState<string[]>([]);
  const [selectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [wishlist, setWishlist] = useState<(Product | import('./services/api').Product)[]>(() => {
    try {
      const saved = localStorage.getItem('wishlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [favoriteShopIds, setFavoriteShopIds] = useState<string[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [showNotifications] = useState(false);
  const [notifications] = useState([
    { id: 1, message: 'Welcome to Quickko! 🎉', type: 'info' },
    { id: 2, message: 'Free delivery on orders over $50! 🚚', type: 'success' },
    { id: 3, message: 'New MDH & Everest spices in stock! 🌶️', type: 'update' }
  ]);
  const [deliveryAddress, setDeliveryAddress] = useState(() => {
    const saved = localStorage.getItem('delivery_address');
    return saved || '885 Walnut St';
  });
  const [, setLoadingAddress] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [userCoupons, setUserCoupons] = useState<any[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  
  // Fetch delivery address from API
  useEffect(() => {
    const loadDeliveryAddress = async () => {
      if (!isAuthenticated || !authUser) {
        // If not authenticated, use localStorage or default
        const saved = localStorage.getItem('delivery_address');
        if (saved) {
          setDeliveryAddress(saved);
        }
        return;
      }

      try {
        setLoadingAddress(true);
        const addresses = await apiService.getUserAddresses();
        
        if (addresses && addresses.length > 0) {
          // Find default address or use first address
          const defaultAddress = addresses.find((addr: any) => addr.is_default) || addresses[0];
          
          // Format address for display
          let formattedAddress = '';
          if (defaultAddress.street) {
            formattedAddress = defaultAddress.street;
            if (defaultAddress.city) {
              formattedAddress += `, ${defaultAddress.city}`;
            }
            if (defaultAddress.state) {
              formattedAddress += `, ${defaultAddress.state}`;
            }
            if (defaultAddress.pincode) {
              formattedAddress += ` ${defaultAddress.pincode}`;
            }
          } else if (defaultAddress.address_line1) {
            // Alternative address format
            formattedAddress = defaultAddress.address_line1;
            if (defaultAddress.address_line2) {
              formattedAddress += `, ${defaultAddress.address_line2}`;
            }
            if (defaultAddress.city) {
              formattedAddress += `, ${defaultAddress.city}`;
            }
            if (defaultAddress.state) {
              formattedAddress += `, ${defaultAddress.state}`;
            }
            if (defaultAddress.postal_code) {
              formattedAddress += ` ${defaultAddress.postal_code}`;
            }
          } else if (defaultAddress.full_address) {
            formattedAddress = defaultAddress.full_address;
          } else {
            // Fallback: use any available address field
            formattedAddress = defaultAddress.address || defaultAddress.location || 'Address not specified';
          }
          
          if (formattedAddress) {
            setDeliveryAddress(formattedAddress);
            localStorage.setItem('delivery_address', formattedAddress);
          }
        }
      } catch (error) {
        console.error('Error loading delivery address from API:', error);
        // Fallback to localStorage if API fails
        const saved = localStorage.getItem('delivery_address');
        if (saved) {
          setDeliveryAddress(saved);
        }
      } finally {
        setLoadingAddress(false);
      }
    };

    loadDeliveryAddress();
  }, [isAuthenticated, authUser]);

  // Load and sync wishlist from localStorage
  useEffect(() => {
    const updateWishlist = () => {
      try {
        const saved = localStorage.getItem('wishlist');
        if (saved) {
          const wishlistData = JSON.parse(saved);
          setWishlist(Array.isArray(wishlistData) ? wishlistData : []);
        } else {
          setWishlist([]);
        }
      } catch (error) {
        console.error('Error loading wishlist:', error);
        setWishlist([]);
      }
    };

    // Load initial wishlist
    updateWishlist();

    // Listen for wishlist updates
    const handleWishlistUpdate = () => {
      updateWishlist();
    };

    window.addEventListener('storage', handleWishlistUpdate);
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);

    // Poll for changes (fallback)
    const interval = setInterval(updateWishlist, 1000);

    return () => {
      window.removeEventListener('storage', handleWishlistUpdate);
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
      clearInterval(interval);
    };
  }, []);
  
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved && ['en', 'te', 'hi', 'ta'].includes(saved) ? saved : 'en';
  });
  const [currency] = useState('INR'); // Always INR
  
  // Listen for language changes from other components
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent) => {
      const newLanguage = event.detail.language;
      setLanguage(prevLang => {
        // Only update if language actually changed
        if (newLanguage !== prevLang && ['en', 'te', 'hi', 'ta'].includes(newLanguage)) {
          return newLanguage;
        }
        return prevLang;
      });
    };
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
  }, []); // Empty dependency array - listener should persist

  // Update document language attribute when language changes and notify other components
  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem('language', language);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
  }, [language]);
  const [showFloatingSearch, setShowFloatingSearch] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [compactMode, setCompactMode] = useState(false);

  // Handle search function
  const handleSearch = () => {
    if (searchQuery.trim()) {
      setCurrentView('search');
    }
  };
  
  // Multi-shop state - use initialView from location state if provided, default to 'main' (Dashboard)
  const locationState = location.state as any;
  const [currentView, setCurrentView] = useState<'discovery' | 'browser' | 'cart' | 'main' | 'order' | 'search' | 'services'>(
    locationState?.view || 'main'
  );
  
  // Fetch coupons from API
  useEffect(() => {
    const loadCoupons = async () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎫 App: Loading coupons...', { currentView, userId: authUser?.id, isAuthenticated });
      }
      
      try {
        setLoadingCoupons(true);
        
        const availableCoupons = await apiService.getAvailableCoupons({
          user_id: authUser?.id,
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🎫 App: Loaded', availableCoupons?.length || 0, 'coupons');
        }
        
        // Load user's clipped coupons if authenticated
        if (isAuthenticated && authUser) {
          try {
            const clipped = await apiService.getUserCoupons(authUser.id);
            setUserCoupons(clipped);
            if (process.env.NODE_ENV === 'development') {
              console.log('🎫 App: Loaded', clipped?.length || 0, 'clipped coupons');
            }
          } catch (err) {
            console.warn('Failed to load user coupons:', err);
            // Don't show error, just continue without user coupons
            setUserCoupons([]);
          }
        }
        
        // Limit to 5 coupons for dashboard display
        const limitedCoupons = availableCoupons?.slice(0, 5) || [];
        setCoupons(limitedCoupons);
      } catch (error: any) {
        console.error('❌ App: Error loading coupons:', error);
        // Keep empty array on error, don't break the UI
        setCoupons([]);
        setCouponError(error?.message || 'Failed to load coupons');
      } finally {
        setLoadingCoupons(false);
      }
    };

    if (currentView === 'main') {
      loadCoupons();
    }
  }, [currentView, authUser?.id, authUser, isAuthenticated]);
  
  // Update view when location state changes
  useEffect(() => {
    const state = location.state as any;
    console.log('🔍 App: Location state changed:', state);
    console.log('🔍 App: Current pathname:', location.pathname);
    if (state?.view) {
      console.log('✅ App: Setting view from location state:', state.view, 'shopId:', state.shopId);
      setCurrentView(state.view);
    } else {
      console.log('⚠️ App: No view in location state, keeping current view:', currentView);
    }
  }, [location.state, location.pathname, currentView]);
  const [multiShopCart, setMultiShopCart] = useState<MultiShopCartItem[]>([]);
  const [savedForLater, setSavedForLater] = useState<MultiShopCartItem[]>([]);
  const [showOrderPlacement, setShowOrderPlacement] = useState(false);
  const [selectedShopForDetails, setSelectedShopForDetails] = useState<Shop | null>(null);
  const [, setShowProfileMenu] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const [selectedProductForCart, setSelectedProductForCart] = useState<ApiProduct | null>(null);
  const [showShopModal, setShowShopModal] = useState(false);

  // Function to load cart from localStorage
  const loadCartFromStorage = () => {
    try {
      const storedCart = localStorage.getItem('multiShopCart');
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        console.log('🛒 App: Loading cart from localStorage');
        console.log('🛒 App: Raw cart data:', parsedCart);
        console.log('🛒 App: Cart type check - isArray:', Array.isArray(parsedCart), 'length:', Array.isArray(parsedCart) ? parsedCart.length : 'N/A');
        
        // Handle both formats: array of items or array of shop carts
        if (Array.isArray(parsedCart) && parsedCart.length > 0) {
          const firstItem = parsedCart[0];
          console.log('🛒 App: First item structure:', {
            hasShop: !!firstItem?.shop,
            hasItems: !!firstItem?.items,
            isItemsArray: Array.isArray(firstItem?.items),
            hasProduct: !!firstItem?.product,
            hasQuantity: firstItem?.quantity !== undefined,
            firstItemKeys: firstItem ? Object.keys(firstItem) : []
          });
          
          // Check if it's the nested format (shop carts with items)
          if (firstItem?.shop && firstItem?.items && Array.isArray(firstItem.items)) {
            // Convert nested format to flat format for App.tsx state
            console.log('🛒 App: Detected NESTED format, converting to flat...');
            const convertedCart: MultiShopCartItem[] = [];
            parsedCart.forEach((shopCart: any, shopIndex: number) => {
              if (shopCart.items && Array.isArray(shopCart.items)) {
                console.log(`🛒 App: Processing shop ${shopIndex}: ${shopCart.shop?.name || 'unknown'}, ${shopCart.items.length} items`);
                shopCart.items.forEach((item: any, itemIndex: number) => {
                  // More lenient validation - check if product exists in any form
                  const product = item.product || item;
                  const shop = shopCart.shop;
                  
                  if (product && shop) {
                    // Ensure product has required fields
                    if (product.id || product.product_id) {
                      const cartItem: MultiShopCartItem = {
                        product: product,
                        shop: shop,
                        quantity: item.quantity || 1
                      };
                      convertedCart.push(cartItem);
                      console.log(`🛒 App: Added item ${itemIndex}: ${product?.name || 'unknown'}, qty: ${cartItem.quantity}`);
                    } else {
                      console.warn(`⚠️ App: Item ${itemIndex} missing product ID:`, { 
                        product: product, 
                        hasId: !!product.id, 
                        hasProductId: !!product.product_id,
                        fullItem: item 
                      });
                    }
                  } else {
                    console.warn(`⚠️ App: Skipping invalid item ${itemIndex} in shop ${shopIndex}:`, { 
                      hasProduct: !!product, 
                      hasShop: !!shop,
                      itemStructure: item,
                      shopCartStructure: shopCart
                    });
                  }
                });
              } else {
                console.warn(`⚠️ App: Shop ${shopIndex} has no items array:`, shopCart);
              }
            });
            console.log('🛒 App: Converted cart items (nested to flat):', convertedCart);
            console.log('🛒 App: Converted cart count:', convertedCart.length);
            
            // Diagnostic: Verify conversion
            const totalQuantity = convertedCart.reduce((sum, item) => sum + item.quantity, 0);
            console.log('🛒 App: Total quantity in converted cart:', totalQuantity);
            console.log('🛒 App: Converted items breakdown:', convertedCart.map(item => ({
              product: item.product?.name || 'unknown',
              shop: item.shop?.name || 'unknown',
              quantity: item.quantity,
              hasProduct: !!item.product,
              hasShop: !!item.shop
            })));
            
            if (convertedCart.length === 0 && parsedCart.length > 0) {
              console.error('❌ App: CONVERSION FAILED! Cart has', parsedCart.length, 'shops but converted to 0 items!');
              console.error('❌ App: First shop cart structure:', parsedCart[0]);
            }
            
            // Save converted cart back to localStorage immediately so header can read it
            try {
              localStorage.setItem('multiShopCart', JSON.stringify(convertedCart));
              console.log('🛒 App: Saved converted cart (nested->flat) back to localStorage');
            } catch (error) {
              console.error('❌ App: Error saving converted cart to localStorage:', error);
            }
            
            setMultiShopCart(convertedCart);
          } else if (firstItem?.product || firstItem?.quantity !== undefined) {
            // Flat format (direct array of items: { product, shop, quantity })
            console.log('🛒 App: Detected FLAT format, using directly');
            console.log('🛒 App: Flat format cart items:', parsedCart);
            console.log('🛒 App: Flat cart count:', parsedCart.length);
            // Validate flat format items
            const validItems = parsedCart.filter((item: any) => {
              const isValid = item.product && item.shop && item.quantity !== undefined;
              if (!isValid) {
                console.warn('⚠️ App: Invalid flat format item:', item);
              }
              return isValid;
            });
            console.log('🛒 App: Valid flat format items:', validItems.length, 'out of', parsedCart.length);
            
            // Diagnostic: Verify items
            const totalQuantity = validItems.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);
            console.log('🛒 App: Total quantity in flat cart:', totalQuantity);
            console.log('🛒 App: Flat items breakdown:', validItems.map((item: any) => ({
              product: item.product?.name || 'unknown',
              shop: item.shop?.name || 'unknown',
              quantity: item.quantity,
              hasProduct: !!item.product,
              hasShop: !!item.shop
            })));
            
            if (validItems.length === 0 && parsedCart.length > 0) {
              console.error('❌ App: VALIDATION FAILED! Cart has', parsedCart.length, 'items but all are invalid!');
              console.error('❌ App: First item structure:', parsedCart[0]);
            }
            
            setMultiShopCart(validItems);
          } else {
            console.warn('⚠️ App: Unknown cart format, first item:', firstItem);
            setMultiShopCart([]);
          }
        } else {
          // Empty cart
          console.log('🛒 App: Cart is empty or invalid format');
          setMultiShopCart([]);
        }
      } else {
        // No cart in storage
        console.log('🛒 App: No cart in localStorage');
        setMultiShopCart([]);
      }
    } catch (error) {
      console.error('❌ App: Error loading multiShopCart from localStorage:', error);
      setMultiShopCart([]);
    }
  };

  // Load multiShopCart from localStorage on mount
  useEffect(() => {
    loadCartFromStorage();
  }, []);

  // Listen for cart updates from other components
  useEffect(() => {
    const handleCartUpdate = () => {
      loadCartFromStorage();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    // Also check periodically in case events are missed
    const interval = setInterval(loadCartFromStorage, 1000);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      clearInterval(interval);
    };
  }, []);

  // Reload cart when cart view is opened
  useEffect(() => {
    if (currentView === 'cart') {
      console.log('🛒 App: Cart view opened, reloading cart from storage');
      // Reload immediately
      loadCartFromStorage();
      // Multiple reloads to ensure we catch the data
      const timeout1 = setTimeout(() => {
        console.log('🛒 App: Secondary cart reload after 100ms');
        loadCartFromStorage();
      }, 100);
      const timeout2 = setTimeout(() => {
        console.log('🛒 App: Tertiary cart reload after 300ms');
        loadCartFromStorage();
      }, 300);
      const timeout3 = setTimeout(() => {
        console.log('🛒 App: Final cart reload after 600ms');
        loadCartFromStorage();
      }, 600);
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
      };
    }
  }, [currentView]);

  // Also reload cart when location state changes to cart view
  useEffect(() => {
    const state = location.state as any;
    if (state?.view === 'cart' || state?.showCart) {
      console.log('🛒 App: Location state changed to cart view, reloading cart');
      loadCartFromStorage();
      if (state?.view === 'cart') {
        setCurrentView('cart');
      } else if (state?.showCart) {
        setShowCart(true);
      }
    }
  }, [location.state]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Load favorite shops from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('favorite_shops');
      setFavoriteShopIds(saved ? JSON.parse(saved) : []);
    } catch {
      setFavoriteShopIds([]);
    }
  }, [currentView]);
  
  // Search state
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Debug current view changes
  useEffect(() => {
    console.log('App: Current view changed to:', currentView);
  }, [currentView]);

  // Token validation is handled centrally by TokenValidator to avoid duplicate calls
  
  
  // API Data State
  const [isApiDataActive, setIsApiDataActive] = useState(false);
  const [apiProducts, setApiProducts] = useState<ApiProduct[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_apiCategories, setApiCategories] = useState<any[]>([]);
  const [level3Categories, setLevel3Categories] = useState<any[]>([]);
  const [isLoadingLevel3Categories, setIsLoadingLevel3Categories] = useState(false);
  const [level2Categories, setLevel2Categories] = useState<any[]>([]);
  const [level2Products, setLevel2Products] = useState<{ [categoryId: string]: ApiProduct[] }>({});
  const [isLoadingLevel2Products, setIsLoadingLevel2Products] = useState(false);
  const [popularProducts, setPopularProducts] = useState<ApiProduct[]>([]);
  const [isLoadingPopularProducts, setIsLoadingPopularProducts] = useState(false);
  const [apiStatus, setApiStatus] = useState<'online' | 'offline' | 'unknown'>('unknown');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isLoading, setIsLoading] = useState(false);

  // Get translations for current language - useMemo ensures it updates when language changes
  const t = useMemo(() => {
    return translations[language as keyof typeof translations] || translations.en;
  }, [language]);
  
  const currentRate = currencyRates[currency as keyof typeof currencyRates];
  const currentSymbol = currencySymbols[currency as keyof typeof currencySymbols];

  const convertPrice = (price: number) => {
    return price * currentRate;
  };

  const formatPrice = (price: number) => {
    return `${currentSymbol}${convertPrice(price).toFixed(2)}`;
  };

  // Removed unused: staticCategories

  // Removed unused: staticSubcategories

  // Removed unused: getCategories function, getMainCategoryFromId

  // Helper function to get appropriate icon for category
  const getCategoryIcon = (categoryName: string): string => {
    const name = categoryName.toLowerCase();
    if (name.includes('grain') || name.includes('flour') || name.includes('rice')) return '🌾';
    if (name.includes('lentil') || name.includes('pulse') || name.includes('dal')) return '🫘';
    if (name.includes('spice') || name.includes('masala')) return '🌶️';
    if (name.includes('oil') || name.includes('ghee')) return '🫒';
    if (name.includes('dairy') || name.includes('milk') || name.includes('curd')) return '🥛';
    if (name.includes('pickle') || name.includes('chutney')) return '🥒';
    if (name.includes('snack') || name.includes('sweet')) return '🍪';
    if (name.includes('nut') || name.includes('seed')) return '🥜';
    if (name.includes('mix') || name.includes('ready')) return '🍽️';
    if (name.includes('frozen') || name.includes('rte')) return '🧊';
    return '🛍️'; // Default icon
  };

  const sampleProducts: Product[] = [
    // Grains & Flours
    { id: 1, name: getTranslatedProductName(1, language), price: 4.99, category: 'grains-flours', topCategory: 'groceries', image: '🌾', rating: 4.8, reviews: 156, inStock: true, discount: 0, brand: 'Aashirvaad', weight: '5kg' },
    { id: 2, name: getTranslatedProductName(2, language), price: 12.99, category: 'grains-flours', topCategory: 'groceries', image: '🍚', rating: 4.6, reviews: 89, inStock: true, discount: 10, brand: 'SWAD', weight: '5kg' },
    { id: 3, name: getTranslatedProductName(3, language), price: 8.99, category: 'grains-flours', topCategory: 'groceries', image: '🍚', rating: 4.4, reviews: 67, inStock: true, discount: 0, brand: 'Generic', weight: '5kg' },
    { id: 4, name: getTranslatedProductName(4, language), price: 3.49, category: 'grains-flours', topCategory: 'groceries', image: '🥣', rating: 4.3, reviews: 45, inStock: true, discount: 0, brand: 'Generic', weight: '1kg' },
    { id: 5, name: getTranslatedProductName(5, language), price: 5.99, category: 'grains-flours', topCategory: 'groceries', image: '⚪', rating: 4.5, reviews: 34, inStock: true, discount: 0, brand: 'Generic', weight: '500g' },

    // Lentils & Pulses
    { id: 6, name: getTranslatedProductName(6, language), price: 6.99, category: 'lentils-pulses', topCategory: 'groceries', image: '🫘', rating: 4.7, reviews: 123, inStock: true, discount: 0, brand: 'SWAD', weight: '1kg' },
    { id: 7, name: getTranslatedProductName(7, language), price: 4.49, category: 'lentils-pulses', topCategory: 'groceries', image: '🫘', rating: 4.4, reviews: 78, inStock: true, discount: 15, brand: 'Generic', weight: '1kg' },
    { id: 8, name: getTranslatedProductName(8, language), price: 3.99, category: 'lentils-pulses', topCategory: 'groceries', image: '🫘', rating: 4.6, reviews: 92, inStock: true, discount: 0, brand: 'Generic', weight: '1kg' },
    { id: 9, name: getTranslatedProductName(9, language), price: 4.99, category: 'lentils-pulses', topCategory: 'groceries', image: '🫘', rating: 4.5, reviews: 56, inStock: true, discount: 0, brand: 'Generic', weight: '1kg' },

    // Spices & Spice Blends
    { id: 10, name: getTranslatedProductName(10, language), price: 2.99, category: 'spices-blends', topCategory: 'groceries', image: '🌶️', rating: 4.8, reviews: 234, inStock: true, discount: 0, brand: 'MDH', weight: '100g' },
    { id: 11, name: getTranslatedProductName(11, language), price: 3.49, category: 'spices-blends', topCategory: 'groceries', image: '🌶️', rating: 4.7, reviews: 189, inStock: true, discount: 10, brand: 'Everest', weight: '100g' },
    { id: 12, name: getTranslatedProductName(12, language), price: 1.99, category: 'spices-blends', topCategory: 'groceries', image: '🟡', rating: 4.6, reviews: 167, inStock: true, discount: 0, brand: 'MDH', weight: '200g' },
    { id: 13, name: getTranslatedProductName(13, language), price: 2.49, category: 'spices-blends', topCategory: 'groceries', image: '🌶️', rating: 4.5, reviews: 145, inStock: true, discount: 0, brand: 'Everest', weight: '100g' },
    { id: 14, name: getTranslatedProductName(14, language), price: 3.99, category: 'spices-blends', topCategory: 'groceries', image: '🌱', rating: 4.7, reviews: 98, inStock: true, discount: 0, brand: 'MDH', weight: '200g' },
    { id: 15, name: getTranslatedProductName(15, language), price: 2.99, category: 'spices-blends', topCategory: 'groceries', image: '🌶️', rating: 4.6, reviews: 76, inStock: true, discount: 15, brand: 'Everest', weight: '100g' },

    // Oils, Ghee & Sweeteners
    { id: 16, name: getTranslatedProductName(16, language), price: 15.99, category: 'oils-ghee', topCategory: 'groceries', image: '🫒', rating: 4.9, reviews: 312, inStock: true, discount: 0, brand: 'Amul', weight: '1kg' },
    { id: 17, name: getTranslatedProductName(17, language), price: 14.99, category: 'oils-ghee', topCategory: 'groceries', image: '🫒', rating: 4.8, reviews: 245, inStock: true, discount: 10, brand: 'Aashirvaad', weight: '1kg' },
    { id: 18, name: getTranslatedProductName(18, language), price: 8.99, category: 'oils-ghee', topCategory: 'groceries', image: '🫒', rating: 4.4, reviews: 89, inStock: true, discount: 0, brand: 'Generic', weight: '1L' },
    { id: 19, name: getTranslatedProductName(19, language), price: 4.99, category: 'oils-ghee', topCategory: 'groceries', image: '🍯', rating: 4.6, reviews: 67, inStock: true, discount: 0, brand: 'Generic', weight: '500g' },

    // Dairy & Fresh Items
    { id: 20, name: getTranslatedProductName(20, language), price: 6.99, category: 'dairy-fresh', topCategory: 'groceries', image: '🧀', rating: 4.7, reviews: 178, inStock: true, discount: 0, brand: 'Amul', weight: '200g' },
    { id: 21, name: getTranslatedProductName(21, language), price: 2.99, category: 'dairy-fresh', topCategory: 'groceries', image: '🫚', rating: 4.5, reviews: 45, inStock: true, discount: 0, brand: 'Fresh', weight: '250g' },
    { id: 22, name: getTranslatedProductName(22, language), price: 1.99, category: 'dairy-fresh', topCategory: 'groceries', image: '🌿', rating: 4.3, reviews: 34, inStock: true, discount: 0, brand: 'Fresh', weight: '100g' },
    { id: 23, name: getTranslatedProductName(23, language), price: 3.49, category: 'dairy-fresh', topCategory: 'groceries', image: '🥛', rating: 4.6, reviews: 89, inStock: true, discount: 0, brand: 'Local', weight: '500g' },

    // Pickles, Chutneys & Condiments
    { id: 24, name: getTranslatedProductName(24, language), price: 4.99, category: 'pickles-chutneys', topCategory: 'groceries', image: '🥭', rating: 4.8, reviews: 156, inStock: true, discount: 0, brand: 'Mother\'s Recipe', weight: '500g' },
    { id: 25, name: getTranslatedProductName(25, language), price: 3.99, category: 'pickles-chutneys', topCategory: 'groceries', image: '🍋', rating: 4.6, reviews: 98, inStock: true, discount: 15, brand: 'Priya', weight: '400g' },
    { id: 26, name: getTranslatedProductName(26, language), price: 5.49, category: 'pickles-chutneys', topCategory: 'groceries', image: '🥒', rating: 4.7, reviews: 87, inStock: true, discount: 0, brand: 'Pachranga', weight: '500g' },
    { id: 27, name: getTranslatedProductName(27, language), price: 2.99, category: 'pickles-chutneys', topCategory: 'groceries', image: '🫚', rating: 4.5, reviews: 67, inStock: true, discount: 0, brand: 'MTR', weight: '200g' },

    // Snacks, Sweets & Frozen RTE
    { id: 28, name: getTranslatedProductName(28, language), price: 3.99, category: 'snacks-sweets', topCategory: 'groceries', image: '🥨', rating: 4.8, reviews: 234, inStock: true, discount: 0, brand: 'Haldiram\'s', weight: '200g' },
    { id: 29, name: getTranslatedProductName(29, language), price: 4.49, category: 'snacks-sweets', topCategory: 'groceries', image: '🍡', rating: 4.7, reviews: 189, inStock: true, discount: 10, brand: 'Haldiram\'s', weight: '250g' },
    { id: 30, name: getTranslatedProductName(30, language), price: 3.99, category: 'snacks-sweets', topCategory: 'groceries', image: '🥨', rating: 4.6, reviews: 145, inStock: true, discount: 0, brand: 'Bikaji', weight: '200g' },
    { id: 31, name: getTranslatedProductName(31, language), price: 2.99, category: 'snacks-sweets', topCategory: 'groceries', image: '🥨', rating: 4.5, reviews: 98, inStock: true, discount: 0, brand: 'Balaji', weight: '150g' },

    // Beverages & Other Essentials
    { id: 32, name: getTranslatedProductName(32, language), price: 5.99, category: 'beverages', topCategory: 'beverages', image: '☕', rating: 4.7, reviews: 167, inStock: true, discount: 0, brand: 'Tata Tea', weight: '500g' },
    { id: 33, name: getTranslatedProductName(33, language), price: 7.99, category: 'beverages', topCategory: 'beverages', image: '☕', rating: 4.6, reviews: 89, inStock: true, discount: 15, brand: 'Generic', weight: '250g' },
    { id: 34, name: getTranslatedProductName(34, language), price: 4.99, category: 'beverages', topCategory: 'beverages', image: '🍹', rating: 4.8, reviews: 234, inStock: true, discount: 0, brand: 'Hamdard', weight: '750ml' },
    { id: 35, name: getTranslatedProductName(35, language), price: 2.99, category: 'beverages', topCategory: 'beverages', image: '🍜', rating: 4.5, reviews: 178, inStock: true, discount: 0, brand: 'Maggi', weight: '70g' },

    // Nuts, Seeds & Dried Fruits
    { id: 36, name: getTranslatedProductName(36, language), price: 12.99, category: 'nuts-seeds', topCategory: 'groceries', image: '🥜', rating: 4.8, reviews: 156, inStock: true, discount: 0, brand: 'Premium', weight: '250g' },
    { id: 37, name: getTranslatedProductName(37, language), price: 15.99, category: 'nuts-seeds', topCategory: 'groceries', image: '🥜', rating: 4.7, reviews: 134, inStock: true, discount: 10, brand: 'Premium', weight: '250g' },
    { id: 38, name: getTranslatedProductName(38, language), price: 6.99, category: 'nuts-seeds', topCategory: 'groceries', image: '🍇', rating: 4.6, reviews: 89, inStock: true, discount: 0, brand: 'Generic', weight: '500g' },
    { id: 39, name: getTranslatedProductName(39, language), price: 4.99, category: 'nuts-seeds', topCategory: 'groceries', image: '🌱', rating: 4.5, reviews: 67, inStock: true, discount: 0, brand: 'Generic', weight: '250g' },

    // Ready Mixes
    { id: 40, name: getTranslatedProductName(40, language), price: 3.99, category: 'ready-mixes', topCategory: 'groceries', image: '🍽️', rating: 4.7, reviews: 123, inStock: true, discount: 0, brand: 'Gits', weight: '200g' },
    { id: 41, name: getTranslatedProductName(41, language), price: 4.49, category: 'ready-mixes', topCategory: 'groceries', image: '🍽️', rating: 4.6, reviews: 98, inStock: true, discount: 15, brand: 'MTR', weight: '250g' },
    { id: 42, name: getTranslatedProductName(42, language), price: 3.99, category: 'ready-mixes', topCategory: 'groceries', image: '🍽️', rating: 4.5, reviews: 76, inStock: true, discount: 0, brand: 'Tasty Bite', weight: '200g' },
    { id: 43, name: getTranslatedProductName(43, language), price: 2.99, category: 'ready-mixes', topCategory: 'groceries', image: '🍽️', rating: 4.4, reviews: 67, inStock: true, discount: 0, brand: 'Aashirvaad', weight: '150g' },

    // Frozen & RTE
    { id: 44, name: getTranslatedProductName(44, language), price: 8.99, category: 'frozen-rte', topCategory: 'groceries', image: '🧊', rating: 4.6, reviews: 89, inStock: true, discount: 0, brand: 'Deep Indian Kitchen', weight: '400g' },
    { id: 45, name: getTranslatedProductName(45, language), price: 6.99, category: 'frozen-rte', topCategory: 'groceries', image: '🧊', rating: 4.5, reviews: 67, inStock: true, discount: 10, brand: 'Haldiram\'s', weight: '300g' },
    { id: 46, name: getTranslatedProductName(46, language), price: 4.99, category: 'frozen-rte', topCategory: 'groceries', image: '🧊', rating: 4.7, reviews: 123, inStock: true, discount: 0, brand: 'Generic', weight: '300g' },
    { id: 47, name: getTranslatedProductName(47, language), price: 5.49, category: 'frozen-rte', topCategory: 'groceries', image: '🧊', rating: 4.4, reviews: 56, inStock: true, discount: 0, brand: 'Generic', weight: '250g' },

    // Household Products
    { id: 48, name: getTranslatedProductName(48, language), price: 1.99, category: 'household', topCategory: 'household', image: '🧽', rating: 4.7, reviews: 156, inStock: true, discount: 0 },
    { id: 49, name: getTranslatedProductName(49, language), price: 2.99, category: 'household', topCategory: 'household', image: '🧻', rating: 4.3, reviews: 94, inStock: true, discount: 20 },
    { id: 50, name: getTranslatedProductName(50, language), price: 8.99, category: 'household', topCategory: 'household', image: '🧺', rating: 4.5, reviews: 89, inStock: true, discount: 0 },
    { id: 51, name: getTranslatedProductName(51, language), price: 3.99, category: 'household', topCategory: 'household', image: '🧹', rating: 4.4, reviews: 67, inStock: true, discount: 10 },

    // Personal Care Products
    { id: 52, name: getTranslatedProductName(52, language), price: 3.49, category: 'personal-care', topCategory: 'personal-care', image: '🦷', rating: 4.4, reviews: 203, inStock: false, discount: 15 },
    { id: 53, name: getTranslatedProductName(53, language), price: 5.99, category: 'personal-care', topCategory: 'personal-care', image: '🧴', rating: 4.6, reviews: 145, inStock: true, discount: 0 },
    { id: 54, name: getTranslatedProductName(54, language), price: 4.49, category: 'personal-care', topCategory: 'personal-care', image: '🧼', rating: 4.3, reviews: 98, inStock: true, discount: 0 },
    { id: 55, name: getTranslatedProductName(55, language), price: 2.99, category: 'personal-care', topCategory: 'personal-care', image: '💨', rating: 4.5, reviews: 76, inStock: true, discount: 20 },

    // Electronics Products
    { id: 56, name: getTranslatedProductName(56, language), price: 299.99, category: 'electronics', topCategory: 'electronics', image: '📱', rating: 4.8, reviews: 342, inStock: true, discount: 25 },
    { id: 57, name: getTranslatedProductName(57, language), price: 599.99, category: 'electronics', topCategory: 'electronics', image: '💻', rating: 4.7, reviews: 234, inStock: true, discount: 0 },
    { id: 58, name: getTranslatedProductName(58, language), price: 49.99, category: 'electronics', topCategory: 'electronics', image: '🎧', rating: 4.6, reviews: 189, inStock: true, discount: 15 },
    { id: 59, name: getTranslatedProductName(59, language), price: 29.99, category: 'electronics', topCategory: 'electronics', image: '🔋', rating: 4.4, reviews: 123, inStock: true, discount: 0 },

    // Clothing Products
    { id: 60, name: getTranslatedProductName(60, language), price: 19.99, category: 'clothing', topCategory: 'clothing', image: '👕', rating: 4.1, reviews: 78, inStock: true, discount: 0 },
    { id: 61, name: getTranslatedProductName(61, language), price: 39.99, category: 'clothing', topCategory: 'clothing', image: '👖', rating: 4.3, reviews: 156, inStock: true, discount: 10 },
    { id: 62, name: getTranslatedProductName(62, language), price: 59.99, category: 'clothing', topCategory: 'clothing', image: '👟', rating: 4.5, reviews: 234, inStock: true, discount: 0 },
    { id: 63, name: getTranslatedProductName(63, language), price: 79.99, category: 'clothing', topCategory: 'clothing', image: '🧥', rating: 4.2, reviews: 98, inStock: true, discount: 20 },

    // Household Products - Laundry Care
    { id: 64, name: 'Laundry Detergent', price: 8.99, category: 'laundry-care', topCategory: 'household', image: '🧺', rating: 4.5, reviews: 234, inStock: true, discount: 0, brand: 'Clean Fresh' },
    { id: 65, name: 'Fabric Softener', price: 5.99, category: 'laundry-care', topCategory: 'household', image: '🧺', rating: 4.3, reviews: 156, inStock: true, discount: 0, brand: 'Clean Fresh' },
    { id: 66, name: 'Stain Remover', price: 4.99, category: 'laundry-care', topCategory: 'household', image: '🧺', rating: 4.4, reviews: 189, inStock: true, discount: 0, brand: 'Clean Fresh' },
    { id: 67, name: 'Dryer Sheets', price: 3.99, category: 'laundry-care', topCategory: 'household', image: '🧺', rating: 4.2, reviews: 123, inStock: true, discount: 0, brand: 'Clean Fresh' },
    { id: 68, name: 'Bleach', price: 2.99, category: 'laundry-care', topCategory: 'household', image: '🧺', rating: 4.1, reviews: 98, inStock: true, discount: 0, brand: 'Clean Fresh' },

    // Household Products - Cleaning Supplies
    { id: 69, name: 'All-Purpose Cleaner', price: 4.99, category: 'cleaning-supplies', topCategory: 'household', image: '🧽', rating: 4.6, reviews: 267, inStock: true, discount: 0, brand: 'Clean Pro' },
    { id: 70, name: 'Glass Cleaner', price: 3.99, category: 'cleaning-supplies', topCategory: 'household', image: '🧽', rating: 4.4, reviews: 198, inStock: true, discount: 0, brand: 'Clean Pro' },
    { id: 71, name: 'Disinfectant Wipes', price: 5.99, category: 'cleaning-supplies', topCategory: 'household', image: '🧽', rating: 4.7, reviews: 312, inStock: true, discount: 0, brand: 'Clean Pro' },
    { id: 72, name: 'Toilet Bowl Cleaner', price: 2.99, category: 'cleaning-supplies', topCategory: 'household', image: '🧽', rating: 4.3, reviews: 145, inStock: true, discount: 0, brand: 'Clean Pro' },
    { id: 73, name: 'Floor Cleaner', price: 6.99, category: 'cleaning-supplies', topCategory: 'household', image: '🧽', rating: 4.5, reviews: 223, inStock: true, discount: 0, brand: 'Clean Pro' },

    // Household Products - Home Decor
    { id: 74, name: 'Wall Art Canvas', price: 25.99, category: 'home-decor', topCategory: 'household', image: '🏠', rating: 4.4, reviews: 156, inStock: true, discount: 0, brand: 'Art Home' },
    { id: 75, name: 'Decorative Vase', price: 18.99, category: 'home-decor', topCategory: 'household', image: '🏠', rating: 4.2, reviews: 134, inStock: true, discount: 0, brand: 'Art Home' },
    { id: 76, name: 'Throw Blanket', price: 32.99, category: 'home-decor', topCategory: 'household', image: '🏠', rating: 4.6, reviews: 289, inStock: true, discount: 0, brand: 'Art Home' },
    { id: 77, name: 'Table Lamp', price: 45.99, category: 'home-decor', topCategory: 'household', image: '🏠', rating: 4.5, reviews: 201, inStock: true, discount: 0, brand: 'Art Home' },
    { id: 78, name: 'Plant Pot', price: 12.99, category: 'home-decor', topCategory: 'household', image: '🏠', rating: 4.3, reviews: 167, inStock: true, discount: 0, brand: 'Art Home' },

    // Household Products - Kitchen Essentials
    { id: 79, name: 'Cutting Board', price: 15.99, category: 'kitchen-essentials', topCategory: 'household', image: '🍳', rating: 4.7, reviews: 298, inStock: true, discount: 0, brand: 'Kitchen Pro' },
    { id: 80, name: 'Chef Knife', price: 29.99, category: 'kitchen-essentials', topCategory: 'household', image: '🍳', rating: 4.8, reviews: 345, inStock: true, discount: 0, brand: 'Kitchen Pro' },
    { id: 81, name: 'Mixing Bowls', price: 18.99, category: 'kitchen-essentials', topCategory: 'household', image: '🍳', rating: 4.5, reviews: 234, inStock: true, discount: 0, brand: 'Kitchen Pro' },
    { id: 82, name: 'Measuring Cups', price: 8.99, category: 'kitchen-essentials', topCategory: 'household', image: '🍳', rating: 4.4, reviews: 189, inStock: true, discount: 0, brand: 'Kitchen Pro' },
    { id: 83, name: 'Can Opener', price: 12.99, category: 'kitchen-essentials', topCategory: 'household', image: '🍳', rating: 4.3, reviews: 156, inStock: true, discount: 0, brand: 'Kitchen Pro' },

    // Household Products - Air Fresheners
    { id: 84, name: 'Room Spray', price: 6.99, category: 'air-fresheners', topCategory: 'household', image: '🌸', rating: 4.2, reviews: 123, inStock: true, discount: 0, brand: 'Fresh Air' },
    { id: 85, name: 'Plug-in Freshener', price: 7.99, category: 'air-fresheners', topCategory: 'household', image: '🌸', rating: 4.4, reviews: 178, inStock: true, discount: 0, brand: 'Fresh Air' },
    { id: 86, name: 'Scented Candles', price: 9.99, category: 'air-fresheners', topCategory: 'household', image: '🌸', rating: 4.6, reviews: 245, inStock: true, discount: 0, brand: 'Fresh Air' },
    { id: 87, name: 'Reed Diffuser', price: 12.99, category: 'air-fresheners', topCategory: 'household', image: '🌸', rating: 4.5, reviews: 201, inStock: true, discount: 0, brand: 'Fresh Air' },
    { id: 88, name: 'Essential Oils', price: 8.99, category: 'air-fresheners', topCategory: 'household', image: '🌸', rating: 4.3, reviews: 167, inStock: true, discount: 0, brand: 'Fresh Air' },

    // Household Products - Pest Control
    { id: 89, name: 'Ant Traps', price: 5.99, category: 'pest-control', topCategory: 'household', image: '🐛', rating: 4.1, reviews: 98, inStock: true, discount: 0, brand: 'Bug Away' },
    { id: 90, name: 'Mouse Traps', price: 8.99, category: 'pest-control', topCategory: 'household', image: '🐛', rating: 4.3, reviews: 145, inStock: true, discount: 0, brand: 'Bug Away' },
    { id: 91, name: 'Insect Spray', price: 7.99, category: 'pest-control', topCategory: 'household', image: '🐛', rating: 4.2, reviews: 134, inStock: true, discount: 0, brand: 'Bug Away' },
    { id: 92, name: 'Fly Paper', price: 3.99, category: 'pest-control', topCategory: 'household', image: '🐛', rating: 4.0, reviews: 89, inStock: true, discount: 0, brand: 'Bug Away' },
    { id: 93, name: 'Cockroach Bait', price: 6.99, category: 'pest-control', topCategory: 'household', image: '🐛', rating: 4.4, reviews: 178, inStock: true, discount: 0, brand: 'Bug Away' },

    // Household Products - Storage Organization
    { id: 94, name: 'Plastic Storage Box', price: 8.99, category: 'storage-organization', topCategory: 'household', image: '📦', rating: 4.5, reviews: 223, inStock: true, discount: 0, brand: 'Organize Pro' },
    { id: 95, name: 'Hanging Organizer', price: 14.99, category: 'storage-organization', topCategory: 'household', image: '📦', rating: 4.3, reviews: 167, inStock: true, discount: 0, brand: 'Organize Pro' },
    { id: 96, name: 'Drawer Dividers', price: 6.99, category: 'storage-organization', topCategory: 'household', image: '📦', rating: 4.4, reviews: 189, inStock: true, discount: 0, brand: 'Organize Pro' },
    { id: 97, name: 'Storage Baskets', price: 12.99, category: 'storage-organization', topCategory: 'household', image: '📦', rating: 4.6, reviews: 256, inStock: true, discount: 0, brand: 'Organize Pro' },
    { id: 98, name: 'File Organizer', price: 9.99, category: 'storage-organization', topCategory: 'household', image: '📦', rating: 4.2, reviews: 134, inStock: true, discount: 0, brand: 'Organize Pro' }
  ];

  // Removed unused function: handleTopCategoryToggle

  // Load subcategories when main category is selected
  useEffect(() => {
    const loadSubcategories = async () => {
      if (isApiDataActive && selectedTopCategory.length > 0) {
        try {
          setIsLoading(true);
          const mainCategoryId = getMainCategoryIdFromName(selectedTopCategory[0]);
          if (mainCategoryId) {
            const subcategories = await apiService.getCategories(mainCategoryId);
            setApiCategories(subcategories);
          }
        } catch (error) {
          console.error('Error loading subcategories:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadSubcategories();
  }, [selectedTopCategory, isApiDataActive]);

  // Load products when subcategory is selected
  useEffect(() => {
    console.log(`🔄 useEffect dependencies changed - selectedCategory: ${selectedCategory}, selectedTopCategory: ${selectedTopCategory}, isApiDataActive: ${isApiDataActive}`);
    
    const loadProductsForSubcategory = async () => {
      console.log(`🔄 useEffect triggered - Loading products for subcategory: ${selectedCategory}, API active: ${isApiDataActive}`);
      
      if (isApiDataActive && selectedCategory !== 'all') {
        console.log(`✅ Condition met - Making API call for subcategory: ${selectedCategory}`);
        try {
          setIsLoading(true);
          console.log(`🌐 Making API call for subcategory: ${selectedCategory}`);
          const products = await apiService.getProducts({
            subcategory: selectedCategory
          });
          console.log(`📦 API returned ${products.length} products for subcategory: ${selectedCategory}`);
          
          if (products.length === 0) {
            console.log(`⚠️ No products found for subcategory: ${selectedCategory}. This subcategory may not exist in the database.`);
          }
          
          setApiProducts(products);
        } catch (error) {
          console.error('❌ Error loading products for subcategory:', error);
        } finally {
          setIsLoading(false);
        }
      } else if (isApiDataActive && selectedCategory === 'all' && selectedTopCategory.length > 0) {
        // Load all products for the main category
        console.log(`🔄 Loading all products for main category: ${selectedTopCategory[0]}`);
        try {
          setIsLoading(true);
          const mainCategoryId = getMainCategoryIdFromName(selectedTopCategory[0]);
          console.log(`🌐 Making API call for main category: ${selectedTopCategory[0]} (ID: ${mainCategoryId})`);
          if (mainCategoryId) {
            const products = await apiService.getProducts({
              main_category_id: mainCategoryId
            });
            console.log(`📦 API returned ${products.length} products for main category: ${selectedTopCategory[0]}`);
            setApiProducts(products);
          }
        } catch (error) {
          console.error('❌ Error loading products for main category:', error);
        } finally {
          setIsLoading(false);
        }
      } else if (isApiDataActive && selectedTopCategory.length === 0) {
        // Load all products when no main category is selected
        console.log(`🔄 Loading all products (no main category selected)`);
        try {
          setIsLoading(true);
          console.log('🌐 Making API call for all products');
          const products = await apiService.getProducts();
          console.log(`📦 API returned ${products.length} products for all categories`);
          setApiProducts(products);
        } catch (error) {
          console.error('❌ Error loading all products:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        console.log('📄 Using static data - no API call needed');
      }
    };

    loadProductsForSubcategory();
  }, [selectedCategory, selectedTopCategory, isApiDataActive]);

  // Helper function to get main category ID from name
  const getMainCategoryIdFromName = (categoryName: string) => {
    const mainCategoryIdMap: { [key: string]: string } = {
      'groceries': '550e8400-e29b-41d4-a716-446655440001',
      'household': '550e8400-e29b-41d4-a716-446655440002',
      'personal-care': '550e8400-e29b-41d4-a716-446655440003',
      'beverages': '550e8400-e29b-41d4-a716-446655440004',
      'electronics': '550e8400-e29b-41d4-a716-446655440005',
      'clothing': '550e8400-e29b-41d4-a716-446655440006'
    };
    return mainCategoryIdMap[categoryName];
  };

  // Choose data source based on API toggle and handle type compatibility
  let filteredProducts: any[] = [];
  
  // Debug logging for data source decision
  console.log(`Data source decision - isApiDataActive: ${isApiDataActive}, apiProducts.length: ${apiProducts.length}, sampleProducts.length: ${sampleProducts.length}`);
  
  if (isApiDataActive && apiProducts.length > 0) {
    // Use API products - they are already filtered by the API call
    console.log('Using API products for filtering');
    filteredProducts = apiProducts.filter((product: any) => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           (product.brand && product.brand.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesSearch;
    });
    
    // Debug logging for API products
    console.log(`API Filtering results: ${filteredProducts.length} products out of ${apiProducts.length}`);
    console.log(`Selected category: ${selectedCategory}, Selected top category: ${selectedTopCategory}`);
  } else {
    // Use static products with enhanced filtering
    console.log('Using static products for filtering');
    filteredProducts = sampleProducts.filter((product: Product) => {
      const matchesTopCategory = selectedTopCategory.length === 0 || selectedTopCategory.includes(product.topCategory);
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           (product.brand && product.brand.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Debug logging
      if (selectedCategory !== 'all') {
        console.log(`Product: ${product.name}, Category: ${product.category}, Selected: ${selectedCategory}, Matches: ${matchesCategory}`);
      }
      
      return matchesTopCategory && matchesCategory && matchesSearch;
    });
    
    // Debug logging
    console.log(`Static Filtering results: ${filteredProducts.length} products out of ${sampleProducts.length}`);
    console.log(`Selected category: ${selectedCategory}, Selected top category: ${selectedTopCategory}`);
  }

  // Removed unused functions: getAvailableCategories, addToCart

  const removeFromCart = (productId: string | number) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId: string | number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item => 
        item.id === productId 
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      return;
    }
    
    // Calculate total amount
    const totalAmount = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Navigate to checkout page with cart data
    navigate('/checkout', {
      state: {
        cartItems: cart,
        totalAmount: totalAmount
      }
    });
    
    // Close the cart sidebar
    setShowCart(false);
  };

  // Removed unused: addToWishlist, removeFromWishlist

  const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  // Removed unused: cartCount, getDiscountedPrice, getCategoryDisplayName

  // Removed unused: getTopCategoryDisplayName, toggleWishlist

  // Multi-shop functions
  const handleAddToMultiShopCart = (product: ShopProduct, shop: Shop) => {
    console.log('Adding to cart:', { product: product.name, shop: shop.name, productId: product.id, shopId: shop.id });
    
    const existingItem = multiShopCart.find(
      item => item.product.id === product.id && item.shop.id === shop.id
    );

    if (existingItem) {
      console.log('Item already in cart, increasing quantity');
      setMultiShopCart(prev => 
        prev.map(item => 
          item.product.id === product.id && item.shop.id === shop.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      console.log('Adding new item to cart');
      setMultiShopCart(prev => [...prev, { product, shop, quantity: 1 }]);
    }
  };

  // Handle shop selection from Popular Products
  const handleShopSelectFromPopular = (shop: Shop, product: ApiProduct) => {
    try {
      // Convert ApiProduct to ShopProduct format
      const shopProduct: ShopProduct = {
        ...product as any,
        id: String(product.id),
        shopId: shop.id,
        shop_id: shop.id,
        category_id: (product as any).category_id || '',
        stock: (product as any).stock_quantity || 0,
        inStock: (product as any).inStock !== undefined ? (product as any).inStock : true,
        createdAt: (product as any).created_at || new Date().toISOString(),
        updatedAt: (product as any).updated_at || new Date().toISOString()
      };

      // Calculate updated cart
      const existingItem = multiShopCart.find(
        item => item.product.id === shopProduct.id && item.shop.id === shop.id
      );

      let updatedCart: MultiShopCartItem[];
      if (existingItem) {
        updatedCart = multiShopCart.map(item => 
          item.product.id === shopProduct.id && item.shop.id === shop.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        updatedCart = [...multiShopCart, { product: shopProduct, shop, quantity: 1 }];
      }
      
      // Update state
      setMultiShopCart(updatedCart);
      
      // Manually save and dispatch event immediately (before useEffect runs)
      try {
        localStorage.setItem('multiShopCart', JSON.stringify(updatedCart));
        console.log('🛒 App: Manually saved cart to localStorage, cart items:', updatedCart.length);
        
        // Dispatch event immediately, then again with delay as fallback
        const event = new CustomEvent('cartUpdated', { detail: { cartCount: updatedCart.length } });
        window.dispatchEvent(event);
        console.log('✅ App: cartUpdated event dispatched immediately, cart count:', updatedCart.length);
        // Also dispatch with small delay as fallback
        setTimeout(() => {
          window.dispatchEvent(event);
          console.log('✅ App: cartUpdated event dispatched (fallback)');
        }, 10);
        
        console.log('✅ Product added to cart from Popular Products:', { product: product.name, shop: shop.name, cartCount: updatedCart.length });
      } catch (error) {
        console.error('Error saving cart:', error);
        throw error;
      }
      
      // Close modal
      setShowShopModal(false);
      setSelectedProductForCart(null);
    } catch (error) {
      console.error('Error adding product to cart:', error);
      alert('Failed to add product to cart. Please try again.');
    }
  };

  // Removed unused: handleUpdateMultiShopQuantity, handleRemoveMultiShopItem

  // Save multiShopCart to localStorage and dispatch update event
  useEffect(() => {
    try {
      if (multiShopCart.length > 0) {
        const cartJson = JSON.stringify(multiShopCart);
        localStorage.setItem('multiShopCart', cartJson);
        // Dispatch cart update event to update header with delay
        setTimeout(() => {
          const event = new CustomEvent('cartUpdated', { detail: { cartCount: multiShopCart.length } });
          window.dispatchEvent(event);
          console.log('🛒 App: Cart saved via useEffect, cartUpdated event dispatched, items:', multiShopCart.length);
        }, 10);
      } else {
        // Only clear localStorage if we're sure it's actually empty
        // Check localStorage first to avoid clearing items that might be in nested format
        const storedCart = localStorage.getItem('multiShopCart');
        if (storedCart) {
          try {
            const parsedCart = JSON.parse(storedCart);
            // If localStorage has items but state is empty, try to reload instead of clearing
            if (Array.isArray(parsedCart) && parsedCart.length > 0) {
              console.warn('⚠️ App: State is empty but localStorage has items, reloading instead of clearing...');
              // Check if it's nested format with actual items
              const firstItem = parsedCart[0];
              let hasActualItems = false;
              
              if (firstItem?.shop && firstItem?.items && Array.isArray(firstItem.items)) {
                // Nested format - check if any shop has items
                hasActualItems = parsedCart.some((shopCart: any) => 
                  shopCart.items && Array.isArray(shopCart.items) && shopCart.items.length > 0
                );
              } else if (firstItem?.product || firstItem?.quantity !== undefined) {
                // Flat format
                hasActualItems = parsedCart.length > 0;
              }
              
              if (hasActualItems) {
                // Try to reload the cart
                setTimeout(() => {
                  loadCartFromStorage();
                }, 50);
                return; // Don't clear localStorage
              }
            }
          } catch (e) {
            console.warn('⚠️ App: Error checking localStorage before clearing:', e);
          }
        }
        // Only clear if localStorage is also empty or invalid
        localStorage.removeItem('multiShopCart');
        setTimeout(() => {
          const event = new CustomEvent('cartUpdated', { detail: { cartCount: 0 } });
          window.dispatchEvent(event);
          console.log('🛒 App: Cart cleared, cartUpdated event dispatched');
        }, 10);
      }
    } catch (error) {
      console.error('❌ App: Error saving multiShopCart to localStorage:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiShopCart]);

  const handleSaveForLater = (productId: string, shopId: string) => {
    const itemToSave = multiShopCart.find(item => 
      item.product.id === productId && item.shop.id === shopId
    );
    
    if (itemToSave) {
      // Add to saved items
      setSavedForLater(prev => [...prev, itemToSave]);
      // Remove from cart
      setMultiShopCart(prev => 
        prev.filter(item => !(item.product.id === productId && item.shop.id === shopId))
      );
    }
  };

  const handleMoveToCart = (productId: string, shopId: string) => {
    const itemToMove = savedForLater.find(item => 
      item.product.id === productId && item.shop.id === shopId
    );
    
    if (itemToMove) {
      // Add to cart
      setMultiShopCart(prev => [...prev, itemToMove]);
      // Remove from saved items
      setSavedForLater(prev => 
        prev.filter(item => !(item.product.id === productId && item.shop.id === shopId))
      );
    }
  };

  const handleRemoveFromSaved = (productId: string, shopId: string) => {
    setSavedForLater(prev => 
      prev.filter(item => !(item.product.id === productId && item.shop.id === shopId))
    );
  };

  const handleMultiShopCheckout = (shopOrders: { shop: Shop; items: MultiShopCartItem[] }[]) => {
    console.log('🛒 Processing multi-shop checkout:', shopOrders);
    console.log('🛒 Current cart items:', multiShopCart);
    
    if (!multiShopCart || multiShopCart.length === 0) {
      console.error('❌ Cannot checkout: cart is empty');
      alert('Your cart is empty. Please add items before checkout.');
      return;
    }
    
    // Convert multi-shop cart items to format expected by CheckoutPage
    const cartItems = multiShopCart.map(item => ({
      id: item.product.id,
      name: item.product.name,
      price: item.product.price,
      quantity: item.quantity,
      image: item.product.image || '',
      brand: item.product.brand || ''
    }));
    
    const totalAmount = multiShopCart.reduce((total, item) => 
      total + (item.product.price * item.quantity), 0
    );
    
    console.log('🛒 Converted cart items for checkout:', cartItems);
    console.log('🛒 Total amount:', totalAmount);
    
    // Navigate to checkout page with cart data
    try {
      navigate('/checkout', {
        state: {
          cartItems,
          totalAmount,
          shopOrders // Also pass shop orders for multi-shop support
        }
      });
      console.log('✅ Navigated to checkout page');
    } catch (error) {
      console.error('❌ Error navigating to checkout:', error);
      alert('Failed to proceed to checkout. Please try again.');
    }
  };

  const handleViewShopDetails = (shop: Shop) => {
    setSelectedShopForDetails(shop);
  };

  const handleCloseShopDetails = () => {
    setSelectedShopForDetails(null);
  };

  const handleOrderPlaced = (orderIds: string[]) => {
    console.log('Orders placed successfully:', orderIds);
    setShowOrderPlacement(false);
    setCurrentView('main');
    setMultiShopCart([]);
    alert(`Orders placed successfully! Order IDs: ${orderIds.join(', ')}`);
  };

  const handleOrderCancel = () => {
    setShowOrderPlacement(false);
    setCurrentView('cart');
  };

  const getMultiShopCartCount = () => {
    // First try to get count from state
    const stateCount = multiShopCart.reduce((total, item) => total + item.quantity, 0);
    
    // Also check localStorage directly as fallback (in case state hasn't synced yet)
    try {
      const storedCart = localStorage.getItem('multiShopCart');
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        let localStorageCount = 0;
        
        if (Array.isArray(parsedCart) && parsedCart.length > 0) {
          // Check if it's the new format (shop carts with items)
          if (parsedCart[0]?.shop && parsedCart[0]?.items) {
            parsedCart.forEach((shopCart: any) => {
              if (shopCart.items && Array.isArray(shopCart.items)) {
                shopCart.items.forEach((item: any) => {
                  localStorageCount += item.quantity || 1;
                });
              }
            });
          } else {
            // Old format (direct array of items)
            localStorageCount = parsedCart.reduce((total: number, item: any) => total + (item.quantity || 1), 0);
          }
        }
        
        // Return the higher count to ensure we don't miss items
        return Math.max(stateCount, localStorageCount);
      }
    } catch (error) {
      console.error('Error reading cart from localStorage in getMultiShopCartCount:', error);
    }
    
    return stateCount;
  };

  // Search functions
  const handleSearchResults = (results: any[]) => {
    setSearchResults(results);
  };

  const handleSearchLoading = (loading: boolean) => {
    setIsSearchLoading(loading);
  };

  const handleSearchError = (error: string | null) => {
    setSearchError(error);
  };

  // API Functions
  const checkApiStatus = async () => {
    try {
      console.log('Checking API status...');
      const response = await apiService.healthCheck();
      console.log('API health check response:', response);
      setApiStatus('online');
    } catch (error) {
      console.error('API health check failed:', error);
      setApiStatus('offline');
    }
  };

  const loadApiData = async () => {
    if (!isApiDataActive || apiStatus === 'offline') return;
    
    setIsLoading(true);
    
    try {
      // Load categories only - products will be loaded by subcategory-specific calls
      const categoriesResponse = await apiService.getCategories();
      setApiCategories(categoriesResponse);
      
      console.log(`Loaded ${categoriesResponse.length} categories from API`);
      setApiStatus('online');
    } catch (error) {
      console.error('Failed to load API data:', error);
      setIsApiDataActive(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Load level 3 categories for quick access section
  useEffect(() => {
    const loadLevel3Categories = async () => {
      try {
        setIsLoadingLevel3Categories(true);
        const categories = await apiService.getCategoriesForSearch(3);
        console.log('📦 Loaded level 3 categories for quick access:', categories.length);
        setLevel3Categories(categories);
      } catch (error) {
        console.error('Error loading level 3 categories:', error);
        setLevel3Categories([]);
      } finally {
        setIsLoadingLevel3Categories(false);
      }
    };

    // Load when component mounts or when switching to main view
    if (currentView === 'main') {
      loadLevel3Categories();
    }
  }, [currentView]);

  // Load level 2 categories and their discounted products for Weekly Deals section
  useEffect(() => {
    let isCancelled = false; // Flag to prevent state updates if component unmounts
    
    const loadLevel2CategoriesAndProducts = async () => {
      try {
        setIsLoadingLevel2Products(true);
        
        // Required categories that should always appear
        const requiredCategoryNames = [
          'Restaurants',
          'Kirana',
          'meat',
          'Bakeries',
          'Home Food Shops',
          'Sweet Shops',
          'Dry Fruit Stores'
        ];
        
        // Step 1: Fetch level 2 categories first - populate deal cards immediately
        const allCategories = await apiService.getCategoriesForSearch(2);
        if (isCancelled) return;
        
        console.log('📦 Loaded level 2 categories for Weekly Deals:', allCategories.length);
        
        // Find required categories from API response (case-insensitive matching)
        const foundRequiredCategories: any[] = [];
        const remainingCategories: any[] = [];
        
        requiredCategoryNames.forEach(requiredName => {
          const found = allCategories.find((cat: any) => {
            const catName = (cat.name || '').toLowerCase();
            const catDisplayName = (cat.display_name || '').toLowerCase();
            const requiredLower = requiredName.toLowerCase();
            return catName === requiredLower || 
                   catDisplayName === requiredLower ||
                   catName.includes(requiredLower) ||
                   catDisplayName.includes(requiredLower);
          });
          
          if (found) {
            // Use database icon first, fallback to getCategoryIcon if missing
            found.icon = found.icon || getCategoryIcon(found.name || found.display_name || requiredName);
            found.display_name = found.display_name || found.name || requiredName;
            foundRequiredCategories.push(found);
          } else {
            // Create fallback category if not found in API
            const fallbackCategory = {
              id: `fallback-${requiredName.toLowerCase().replace(/\s+/g, '-')}`,
              name: requiredName,
              display_name: requiredName,
              icon: getCategoryIcon(requiredName), // Use getCategoryIcon for fallback categories
              top_category: 'groceries',
              lvlcategory: 2,
              level: 2
            };
            foundRequiredCategories.push(fallbackCategory);
            console.log(`⚠️ Category "${requiredName}" not found in API, using fallback`);
          }
        });
        
        // Add remaining categories (excluding already found ones)
        allCategories.forEach((cat: any) => {
          const isRequired = foundRequiredCategories.some(req => 
            req.id === cat.id || 
            (req.name || '').toLowerCase() === (cat.name || '').toLowerCase()
          );
          if (!isRequired) {
            remainingCategories.push(cat);
          }
        });
        
        // Combine: required categories first, then others (limit to 7 total)
        const categoriesToLoad = [
          ...foundRequiredCategories.slice(0, 7),
          ...remainingCategories.slice(0, Math.max(0, 7 - foundRequiredCategories.length))
        ];
        
        console.log(`✅ Weekly Deals categories: ${categoriesToLoad.length} total (${foundRequiredCategories.length} required)`);
        if (isCancelled) return;
        
        setLevel2Categories(categoriesToLoad);
        
        // Initialize empty products map
        const productsMap: { [categoryId: string]: ApiProduct[] } = {};
        categoriesToLoad.forEach(category => {
          productsMap[category.id] = [];
        });
        setLevel2Products(productsMap);
        
        // Cache to prevent duplicate API calls for the same Level 3 ID
        // Removed unused: level3ProductsCache, level3FetchPromises
        
        // Step 2: Fetch discounted products for each category asynchronously
        await Promise.all(
          categoriesToLoad.map(async (category) => {
            try {
              let allProducts: ApiProduct[] = [];
              const isFallbackCategory = category.id && category.id.startsWith('fallback-');
              
              if (isFallbackCategory) {
                // For fallback categories, search by category name
                try {
                  allProducts = await apiService.getProducts({
                    category: category.name || category.display_name,
                    limit: 20
                  });
                  console.log(`✅ Loaded ${allProducts.length} products using category name for fallback ${category.name}`);
                } catch (fallbackError) {
                  console.log(`⚠️ Category name search failed for ${category.name}, trying subcategory...`);
                  try {
                    allProducts = await apiService.getProducts({
                      subcategory: category.name || category.display_name,
                      limit: 20
                    });
                    console.log(`✅ Loaded ${allProducts.length} products using subcategory name for ${category.name}`);
                  } catch (subError) {
                    console.error(`❌ All methods failed for fallback category ${category.name}:`, subError);
                    allProducts = [];
                  }
                }
              } else {
                // Step 1: Get all Level 3 categories for this Level 2 category
                let level3Categories: any[] = [];
                try {
                  level3Categories = await apiService.getCategoriesForSearch(3, category.id);
                  console.log(`📦 Category "${category.name}": Found ${level3Categories.length} Level 3 categories from API`);
                  
                  // CRITICAL: Validate that Level 3 categories actually belong to this Level 2 category
                  const validatedLevel3Categories = level3Categories.filter((level3Cat: any) => {
                    const parentIdMatches = level3Cat.parent_id === category.id;
                    if (!parentIdMatches) {
                      console.warn(`⚠️ Level 3 category "${level3Cat.name}" (ID: ${level3Cat.id}) has parent_id "${level3Cat.parent_id}" but expected "${category.id}" - FILTERING OUT`);
                    }
                    return parentIdMatches;
                  });
                  
                  level3Categories = validatedLevel3Categories;
                  console.log(`✅ Category "${category.name}": Validated ${level3Categories.length} Level 3 categories with correct parent_id`);
                  
                  if (level3Categories.length > 0) {
                    console.log(`   Level 3 category names:`, level3Categories.slice(0, 5).map((c: any) => c.name || c.display_name));
                    console.log(`   Level 3 category IDs:`, level3Categories.slice(0, 5).map((c: any) => c.id));
                  }
                } catch (level3Error) {
                  console.log(`⚠️ Failed to get Level 3 categories for ${category.name}, trying fallback...`);
                  // Fallback: try by parent category name
                  try {
                    level3Categories = await apiService.getCategoriesForSearch(3, undefined, category.name || category.display_name);
                    console.log(`✅ Found ${level3Categories.length} Level 3 categories using parent name`);
                    
                    // Still validate parent_id even for fallback
                    const validatedLevel3Categories = level3Categories.filter((level3Cat: any) => {
                      const parentIdMatches = level3Cat.parent_id === category.id;
                      if (!parentIdMatches) {
                        console.warn(`⚠️ Level 3 category "${level3Cat.name}" (ID: ${level3Cat.id}) has parent_id "${level3Cat.parent_id}" but expected "${category.id}" - FILTERING OUT`);
                      }
                      return parentIdMatches;
                    });
                    
                    level3Categories = validatedLevel3Categories;
                    console.log(`✅ Category "${category.name}": Validated ${level3Categories.length} Level 3 categories with correct parent_id (fallback)`);
                  } catch (nameError) {
                    console.error(`❌ Failed to get Level 3 categories for ${category.name}`);
                    level3Categories = [];
                  }
                }
                
                // Step 2: Get products for all Level 3 categories in a single optimized API call
                // SQL equivalent: SELECT * FROM products WHERE category_id IN (Level3_IDs) AND discount > 0
                if (level3Categories.length > 0) {
                  // Deduplicate Level 3 IDs
                  const level3Ids = Array.from(new Set(level3Categories.map((cat: any) => cat.id)));
                  console.log(`🔍 Category "${category.name}": Fetching products for ${level3Ids.length} unique Level 3 categories (${level3Categories.length} total)`);
                  console.log(`   Level 3 IDs:`, level3Ids.slice(0, 3));
                  
                  // OPTIMIZED: Single API call for all Level 3 IDs (Option 1 - Best Performance)
                  try {
                    const fetchedProducts = await apiService.getProductsByCategoryIds(
                      level3Ids,
                      {
                        discount: true, // Filter by discount > 0 (client-side if API doesn't support)
                        rating: 3.0, // Filter by rating >= 3.0
                        limit: 200, // Get more products to have better selection
                      }
                    );
                    
                    // CRITICAL: Validate that products belong to one of this category's Level 3 categories
                    const validatedLevel3Ids = new Set(level3Ids);
                    const validatedProducts = fetchedProducts.filter((product: ApiProduct) => {
                      const productCategoryId = product.category_id;
                      const belongsToLevel3 = productCategoryId && validatedLevel3Ids.has(productCategoryId);
                      
                      if (!belongsToLevel3 && productCategoryId) {
                        console.warn(`⚠️ Category "${category.name}": Product "${product.name}" (ID: ${product.id}) has category_id "${productCategoryId}" which is NOT in Level 3 categories for "${category.name}" - FILTERING OUT`);
                      }
                      
                      return belongsToLevel3;
                    });
                    
                    // Remove duplicates by product ID
                    const uniqueProducts = validatedProducts.filter((product, index, self) =>
                      index === self.findIndex(p => p.id === product.id)
                    );
                    
                    allProducts = uniqueProducts;
                    console.log(`📦 Category "${category.name}": Total validated unique products: ${allProducts.length} (from ${fetchedProducts.length} fetched)`);
                    if (allProducts.length > 0) {
                      console.log(`📦 Category "${category.name}": Sample products:`, allProducts.slice(0, 3).map(p => p.name));
                    } else if (fetchedProducts.length > 0) {
                      console.warn(`⚠️ Category "${category.name}": All ${fetchedProducts.length} products were filtered out because they don't belong to validated Level 3 categories!`);
                    }
                  } catch (error) {
                    console.error(`❌ Failed to fetch products for category "${category.name}":`, error);
                    allProducts = [];
                  }
                } else {
                  // Fallback: If no Level 3 categories found, try direct Level 2 approach
                  console.log(`⚠️ No Level 3 categories found, trying direct Level 2 approach...`);
                  try {
                    allProducts = await apiService.getProducts({
                      subcategory_id: category.id,
                      limit: 50
                    });
                    console.log(`✅ Loaded ${allProducts.length} products using subcategory_id for ${category.name}`);
                  } catch (subError) {
                    console.log(`⚠️ subcategory_id failed for ${category.name}, trying subcategory name...`);
                    try {
                      allProducts = await apiService.getProducts({
                        subcategory: category.name || category.display_name,
                        limit: 50
                      });
                      console.log(`✅ Loaded ${allProducts.length} products using subcategory name for ${category.name}`);
                    } catch (nameError) {
                      console.error(`❌ All methods failed for ${category.name}:`, nameError);
                      allProducts = [];
                    }
                  }
                }
              }
              
              // Step 3: Filter for discounted products with rating >= 3.0 (client-side filtering as requested)
              // SQL equivalent: WHERE category_id IN (Level3_IDs) AND discount > 0
              const discountedProducts = allProducts
                .filter((p: ApiProduct) => {
                  const hasDiscount = (p.discount || 0) > 0; // Client-side filter: discount > 0
                  const hasRating = (p.rating || 0) >= 3.0;
                  return hasDiscount && hasRating;
                })
                .sort((a: ApiProduct, b: ApiProduct) => (b.discount || 0) - (a.discount || 0))
                .slice(0, 5); // Take top 5 discounted products
              
              // Step 4: If no discounted products, use regular products with rating >= 3.0 as fallback
              const regularProducts = allProducts
                .filter((p: ApiProduct) => (p.rating || 0) >= 3.0)
                .slice(0, 5);
              
              const productsToShow = discountedProducts.length > 0 
                ? discountedProducts 
                : regularProducts;
              
              productsMap[category.id] = productsToShow;
              
              console.log(`📦 Category: ${category.name} (${category.id})`);
              console.log(`   - Total products: ${allProducts.length}`);
              console.log(`   - Discounted products (rating >= 3.0): ${discountedProducts.length}`);
              console.log(`   - Regular products (rating >= 3.0): ${regularProducts.length}`);
              console.log(`   - Products to show: ${productsToShow.length}`);
              if (productsToShow.length > 0) {
                console.log(`   - Product names:`, productsToShow.map(p => p.name));
                console.log(`   - Product IDs:`, productsToShow.map(p => p.id));
              } else {
                console.warn(`⚠️ Category "${category.name}" has NO products to show!`);
              }
              
              // Update state for this category
              setLevel2Products(prev => {
                const updated = {
                  ...prev,
                  [category.id]: productsToShow
                };
                console.log(`✅ Updated state for category "${category.name}": ${productsToShow.length} products`);
                return updated;
              });
            } catch (error) {
              console.error(`Error loading products for category ${category.name}:`, error);
              // Keep empty array for this category
            }
          })
        );
      } catch (error) {
        console.error('Error loading level 2 categories and products:', error);
        setLevel2Categories([]);
        setLevel2Products({});
      } finally {
        setIsLoadingLevel2Products(false);
      }
    };

    // Load when component mounts or when switching to main view
    if (currentView === 'main') {
      loadLevel2CategoriesAndProducts();
    }
    
    // Cleanup function to cancel ongoing requests
    return () => {
      isCancelled = true;
    };
  }, [currentView]);

  // Load popular products with rating >= 4.5 for Popular Products section
  useEffect(() => {
    const loadPopularProducts = async () => {
      try {
        setIsLoadingPopularProducts(true);
        
        // Fetch all products from API
        const allProducts = await apiService.getProducts({ limit: 100 });
        console.log('📦 Loaded products for Popular Products:', allProducts.length);
        
        // Filter products with rating >= 4.5
        const highRatedProducts = allProducts.filter((product: ApiProduct) => {
          const rating = product.rating || 0;
          return rating >= 4.5;
        });
        
        // Sort by rating (highest first) and limit to 12 products
        const sortedProducts = highRatedProducts
          .sort((a: ApiProduct, b: ApiProduct) => (b.rating || 0) - (a.rating || 0))
          .slice(0, 12);
        
        console.log(`⭐ Loaded ${sortedProducts.length} popular products with rating >= 4.5 (out of ${allProducts.length} total products)`);
        setPopularProducts(sortedProducts);
      } catch (error) {
        console.error('Error loading popular products:', error);
        setPopularProducts([]);
      } finally {
        setIsLoadingPopularProducts(false);
      }
    };

    // Load when component mounts or when switching to main view
    if (currentView === 'main') {
      loadPopularProducts();
    }
  }, [currentView]);

  // Removed unused: handleDataSourceToggle

  // Effects
  useEffect(() => {
    checkApiStatus();
  }, []);

  useEffect(() => {
    if (isApiDataActive) {
      // Only load initial data when API is first activated
      // Don't reload on every category change - let the subcategory useEffect handle that
      loadApiData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApiDataActive]);

  // Auto-refresh API data every 30 seconds when using API data
  useEffect(() => {
    if (isApiDataActive && apiStatus === 'online') {
      const interval = setInterval(() => {
        console.log('Auto-refreshing API data...');
        loadApiData();
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApiDataActive, apiStatus]);

  return (
    <div className={`App ${compactMode ? 'compact-mode' : ''}`} role="application">
      {/* Skip Links for Accessibility */}
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <a href="#navigation" className="skip-link">Skip to navigation</a>
      
      {/* ARIA Live Region for announcements */}
      <div className="aria-live" aria-live="polite" aria-atomic="true" id="announcements"></div>
      {/* Sticky Header */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        cartCount={getMultiShopCartCount()}
        notificationCount={notifications.length}
        // Removed onSearch prop to allow default navigation to /search route
        onCartClick={() => setCurrentView('cart')}
      />

      {/* Delivery Address Bar - Only in Dashboard */}
      {currentView === 'main' && (
        <nav className="delivery-address-bar" aria-label="Delivery location">
          <div className="body-container">
            <button 
              className="address-selector"
              onClick={() => setShowAddressModal(true)}
              aria-label={`Change delivery address. Current address: ${deliveryAddress}`}
            >
              <span className="address-icon">📍</span>
              <span className="address-label">{t.deliverTo || 'Deliver to'}</span>
              <span className="address-value">{deliveryAddress}</span>
              <span className="address-arrow" aria-hidden="true">▼</span>
            </button>
          </div>
        </nav>
      )}

      {/* Simplified Navigation */}
      <nav id="navigation" className="main-nav" role="navigation" aria-label="Primary">
        <div className="nav-content">
          {/* Direct Navigation Buttons */}
          <div className="nav-buttons">
            <button 
              className={`nav-btn ${currentView === 'main' ? 'active' : ''}`} 
              onClick={() => setCurrentView('main')}
            >
              🏠 {t.mainStore || 'Dashboard'}
            </button>
            <button 
              className={`nav-btn ${currentView === 'discovery' ? 'active' : ''}`} 
              onClick={() => setCurrentView('discovery')}
            >
              🔎 {t.exploreShops || 'Explore Shops'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/browse-all' || location.pathname === '/browse' ? 'active' : ''}`} 
              onClick={() => navigate('/browse-all')}
            >
              🛍️ {t.browseAll || 'Browse All'}
            </button>
            <button 
              className="nav-btn" 
              onClick={() => navigate('/services')}
            >
              🔧 {language === 'hi' ? 'सेवाएं' : language === 'te' ? 'సేవలు' : language === 'ta' ? 'சேவைகள்' : 'Services'}
            </button>
          </div>
        </div>
      </nav>

      {/* My Account Section - Only in Dashboard, after navigation buttons */}
      {currentView === 'main' && isAuthenticated && (
        <section className="user-summary card" aria-label="Your summary">
          <h2>My Account</h2>
          <div className="summary-grid">
            <div 
              className="summary-item clickable"
              onClick={() => navigate('/loyalty')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/loyalty');
                }
              }}
              aria-label="View Loyalty Points"
            >
              <div className="summary-icon">⭐</div>
              <div className="summary-text">
                <h4>Loyalty Points</h4>
                <p>{(authUser as any)?.loyalty_points ?? (authUser as any)?.points ?? 0}</p>
              </div>
            </div>
            <div 
              className="summary-item clickable"
              onClick={() => navigate('/wishlist')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/wishlist');
                }
              }}
              aria-label="View Wishlist"
            >
              <div className="summary-icon">❤️</div>
              <div className="summary-text">
                <h4>Wishlist</h4>
                <p>{wishlist.length} items</p>
              </div>
            </div>
            <div 
              className="summary-item clickable"
              onClick={() => navigate('/favorite-shops')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/favorite-shops');
                }
              }}
              aria-label="View Favorite Shops"
            >
              <div className="summary-icon">❤️</div>
              <div className="summary-text">
                <h4>Favorite Shops</h4>
                <p>{favoriteShopIds.length} {favoriteShopIds.length === 1 ? 'shop' : 'shops'}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Special Occasions Banner - Only in Dashboard */}
      {currentView === 'main' && (
        <section className="special-occasions-banner-section" aria-label="Special Occasions">
          <div className="special-occasion-banner">
            <div className="banner-content">
              <h2 className="banner-title">New Year's</h2>
              <p className="banner-subtitle">Celebration Essentials</p>
            </div>
            <div className="banner-decorations">
              <span className="decoration-star star-1">⭐</span>
              <span className="decoration-star star-2">⭐</span>
              <span className="decoration-star star-3">⭐</span>
              <span className="decoration-confetti confetti-1">🎊</span>
              <span className="decoration-confetti confetti-2">🎉</span>
              <span className="decoration-party-hat">🎩</span>
              <span className="decoration-champagne">🥂</span>
            </div>
          </div>
        </section>
      )}

      {/* Quick Access Icons Section - Only in Dashboard */}
      {currentView === 'main' && (
        <section className="quick-access-section" aria-label="Quick Access">
          <div className="quick-access-header">
            <h2 className="quick-access-title">Quick Access</h2>
            <button type="button" className="view-all-products-link" onClick={() => navigate('/browse-all')}>
              View All Products →
            </button>
          </div>
          <div className="quick-access-carousel">
            <button 
              className="carousel-nav-btn carousel-prev" 
              onClick={() => {
                const container = document.querySelector('.quick-access-container');
                if (container) {
                  container.scrollBy({ left: -400, behavior: 'smooth' });
                }
              }}
              aria-label="Previous items"
            >
              ‹
            </button>
            <div className="quick-access-container">
              <div 
                className="quick-access-item" 
                onClick={() => navigate('/quickko-plus')}
                style={{ cursor: 'pointer' }}
              >
                <div className="quick-access-icon">
                  <div className="icon-circle">🎖️</div>
                </div>
                <span className="quick-access-label">Quickko Plus</span>
              </div>
              <div className="quick-access-item">
                <div className="quick-access-icon">
                  <div className="icon-circle">⏰</div>
                </div>
                <span className="quick-access-label">Order Ahead</span>
              </div>
              <div 
                className="quick-access-item" 
                onClick={() => navigate('/weekly-ad')}
                style={{ cursor: 'pointer' }}
              >
                <div className="quick-access-icon">
                  <div className="icon-circle">🏷️</div>
                </div>
                <span className="quick-access-label">Weekly Specials</span>
              </div>
              <div className="quick-access-item">
                <div className="quick-access-icon">
                  <div className="icon-circle">📝</div>
                </div>
                <span className="quick-access-label">Shopping List</span>
              </div>
              <div 
                className="quick-access-item" 
                onClick={() => navigate('/explore-shops', { state: { shopType: 'pharmacy' } })}
                style={{ cursor: 'pointer' }}
              >
                <div className="quick-access-icon">
                  <div className="icon-circle">💊</div>
                </div>
                <span className="quick-access-label">Pharmacy</span>
              </div>
              <div className="quick-access-item active">
                <div className="quick-access-icon">
                  <div className="icon-circle">✅</div>
                </div>
                <span className="quick-access-label">My Nutrition Insights</span>
              </div>
              {/* Level 3 Categories from API */}
              {isLoadingLevel3Categories ? (
                <div className="quick-access-item">
                  <div className="quick-access-icon">
                    <div className="icon-circle">⏳</div>
                  </div>
                  <span className="quick-access-label">Loading...</span>
                </div>
              ) : (
                level3Categories.map((category) => {
                  const categoryIcon = category.icon || getCategoryIcon(category.name || category.display_name || '');
                  const categoryName = category.display_name || category.name || 'Unknown';
                  const categoryId = category.id || category.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown';
                  
                  return (
                    <div 
                      key={categoryId} 
                      className="quick-access-item" 
                      onClick={() => navigate('/browse-all', { 
                        state: { 
                          categoryId: categoryId, 
                          categoryName: categoryName,
                          filterType: 'category' 
                        } 
                      })}
                    >
                      <div className="quick-access-icon">
                        <div className="icon-circle">{categoryIcon}</div>
                      </div>
                      <span className="quick-access-label">{categoryName}</span>
                    </div>
                  );
                })
              )}
            </div>
            <button 
              className="carousel-nav-btn carousel-next" 
              onClick={() => {
                const container = document.querySelector('.quick-access-container');
                if (container) {
                  container.scrollBy({ left: 400, behavior: 'smooth' });
                }
              }}
              aria-label="Next items"
            >
              ›
            </button>
          </div>
        </section>
      )}

      {/* Popular Products Section - Only in Dashboard */}
      {currentView === 'main' && (
        <section className="popular-products-section" aria-label="Popular Products">
          <div className="popular-products-container">
            <h2 className="popular-products-title">Must Have Fresh Meat & Produce Deals</h2>
            {isLoadingPopularProducts ? (
              <div className="popular-products-loading">
                <p>Loading popular products...</p>
              </div>
            ) : popularProducts.length > 0 ? (
              <div className="popular-products-carousel">
                <button 
                  className="carousel-nav-btn carousel-prev" 
                  onClick={() => {
                    const carousel = document.querySelector('.popular-products-scroll');
                    if (carousel) {
                      carousel.scrollBy({ left: -400, behavior: 'smooth' });
                    }
                  }}
                  aria-label="Previous products"
                >
                  ‹
                </button>
                <div className="popular-products-scroll">
                  {popularProducts.map((product) => {
                    const productImage = product.image || 'https://via.placeholder.com/300x300?text=Product';
                    const productPrice = product.price || 0;
                    const originalPrice = (product as any).originalPrice || (product as any).original_price;
                    const discount = product.discount || (originalPrice && productPrice ? Math.round(((originalPrice - productPrice) / originalPrice) * 100) : undefined);
                    const categoryName = product.category || (product as any).category_name || '';
                    const categoryLabel = categoryName.toUpperCase();
                    const productName = product.name || 'Product';
                    const productUnit = product.weight || '1 each';
                    const rating = product.rating || 0;
                    
                    return (
                      <div 
                        key={product.id} 
                        className="popular-product-card"
                        onClick={() => navigate('/browse-all', { 
                          state: { 
                            searchQuery: productName,
                            filterType: 'search' 
                          } 
                        })}
                      >
                        <div className="product-image-container">
                          <img 
                            src={productImage} 
                            alt={productName}
                            className="product-image"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = `https://via.placeholder.com/300x300?text=${encodeURIComponent(productName)}`;
                            }}
                          />
                          {categoryLabel && (
                            <div className="product-category-label">{categoryLabel}</div>
                          )}
                          {rating >= 4.5 && (
                            <div className="product-rating-badge">
                              ⭐ {rating.toFixed(1)}
                            </div>
                          )}
                        </div>
                        <div className="product-price-section">
                          {originalPrice && originalPrice > productPrice ? (
                            <>
                              <span className="product-price sale">{formatPrice(productPrice)}</span>
                              <span className="product-original-price">{formatPrice(originalPrice)}</span>
                            </>
                          ) : (
                            <span className="product-price">{formatPrice(productPrice)}</span>
                          )}
                        </div>
                        <h3 className="product-name">{productName}</h3>
                        <div className="product-unit">{productUnit}</div>
                        <div className="product-payment">SNAP EBT</div>
                        {discount && discount > 0 && (
                          <button className="product-deal-btn">{discount}% OFF</button>
                        )}
                        <button 
                          className="product-action-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            // Show shop selection modal
                            setSelectedProductForCart(product);
                            setShowShopModal(true);
                          }}
                        >
                          Add to Cart
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button 
                  className="carousel-nav-btn carousel-next" 
                  onClick={() => {
                    const carousel = document.querySelector('.popular-products-scroll');
                    if (carousel) {
                      carousel.scrollBy({ left: 400, behavior: 'smooth' });
                    }
                  }}
                  aria-label="Next products"
                >
                  ›
                </button>
              </div>
            ) : (
              <div className="popular-products-empty">
                <p>No popular products available at the moment.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Weekly Deals Section - Only in Dashboard */}
      {currentView === 'main' && (
        <section className="weekly-deals-section" aria-label="Weekly Deals">
          <div className="weekly-deals-container">
            <div className="weekly-deals-header">
              <h2 className="weekly-deals-title">Weekly Deals</h2>
              <button type="button" className="view-all-deals-link" onClick={() => navigate('/weekly-ad')}>
                View All Deals →
              </button>
            </div>
            
            {isLoadingLevel2Products ? (
              <div className="weekly-deals-loading">
                <p>Loading deals...</p>
              </div>
            ) : level2Categories.length > 0 ? (
              <div className="weekly-deals-grid">
                {level2Categories.slice(0, 6).map((category) => {
                  const categoryId = category.id || 'unknown';
                  const categoryName = category.display_name || category.name || 'Category';
                  const products = level2Products[categoryId] || [];
                  const categoryIcon = category.icon || getCategoryIcon(categoryName);
                  
                  // Get deal class based on category name
                  const getDealClass = (name: string) => {
                    const lowerName = name.toLowerCase();
                    if (lowerName.includes('grocery') || lowerName.includes('food') || lowerName.includes('fresh')) return 'groceries-deal';
                    if (lowerName.includes('laundry') || lowerName.includes('cleaning')) return 'laundry-deal';
                    if (lowerName.includes('pet')) return 'pets-deal';
                    if (lowerName.includes('personal') || lowerName.includes('care') || lowerName.includes('beauty')) return 'personalcare-deal';
                    if (lowerName.includes('beverage') || lowerName.includes('drink')) return 'beverages-deal';
                    return 'groceries-deal'; // Default
                  };
                  
                  // Get deal button class
                  const getDealButtonClass = (name: string) => {
                    const lowerName = name.toLowerCase();
                    if (lowerName.includes('grocery') || lowerName.includes('food') || lowerName.includes('fresh')) return 'groceries-btn';
                    if (lowerName.includes('laundry') || lowerName.includes('cleaning')) return 'laundry-btn';
                    if (lowerName.includes('pet')) return 'pets-btn';
                    if (lowerName.includes('personal') || lowerName.includes('care') || lowerName.includes('beauty')) return 'personalcare-btn';
                    if (lowerName.includes('beverage') || lowerName.includes('drink')) return 'beverages-btn';
                    return 'groceries-btn'; // Default
                  };
                  
                  return (
                    <div key={categoryId} className={`deal-banner ${getDealClass(categoryName)}`}>
                      <div className="deal-banner-content">
                        <button 
                          className={`deal-cta-btn ${getDealButtonClass(categoryName)}`} 
                          onClick={() => navigate('/browse-all', { 
                            state: { 
                              categoryId: categoryId, 
                              categoryName: categoryName,
                              filterType: 'category' 
                            } 
                          })}
                        >
                          SHOP NOW &gt;
                        </button>
                        <div className="deal-products">
                          {products.length > 0 ? (
                            products.slice(0, 5).map((product, idx) => {
                              const productPrice = product.price || 0;
                              const productDiscount = product.discount || 0;
                              const discountedPrice = productDiscount > 0 
                                ? Math.round(productPrice * (1 - productDiscount / 100))
                                : productPrice;
                              const productRating = product.rating || 0;
                              const ratingStars = Math.round(productRating);
                              
                              return (
                                <div key={product.id || idx} className="deal-product-item-wrapper">
                                  <div className="deal-product-item">
                                    {product.image ? (
                                      <img 
                                        src={product.image} 
                                        alt={product.name || 'Product'} 
                                        className="deal-product-image"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const parent = target.parentElement;
                                          if (parent && !parent.querySelector('.deal-product-fallback')) {
                                            const fallback = document.createElement('span');
                                            fallback.className = 'deal-product-fallback';
                                            fallback.textContent = categoryIcon;
                                            parent.appendChild(fallback);
                                          }
                                        }}
                                      />
                                    ) : (
                                      <span className="deal-product-fallback">{categoryIcon}</span>
                                    )}
                                  </div>
                                  <div className="deal-product-details">
                                    <div className="deal-product-name" title={product.name || 'Product'}>
                                      {product.name || 'Product'}
                                    </div>
                                    <div className="deal-product-pricing">
                                      {productDiscount > 0 ? (
                                        <>
                                          <span className="deal-product-price-discounted">
                                            ₹{discountedPrice.toLocaleString('en-IN')}
                                          </span>
                                          <span className="deal-product-price-original">
                                            ₹{Math.round(productPrice).toLocaleString('en-IN')}
                                          </span>
                                        </>
                                      ) : (
                                        <span className="deal-product-price">
                                          ₹{Math.round(productPrice).toLocaleString('en-IN')}
                                        </span>
                                      )}
                                    </div>
                                    <div className="deal-product-rating">
                                      <span className="deal-rating-stars">
                                        {'⭐'.repeat(Math.min(ratingStars, 5))}
                                      </span>
                                      <span className="deal-rating-number">
                                        {productRating.toFixed(1)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            // Fallback to icons if no products
                            <>
                              <div className="deal-product-item-wrapper">
                                <div className="deal-product-item">
                                  <span className="deal-product-fallback">{categoryIcon}</span>
                                </div>
                                <div className="deal-product-details">
                                  <div className="deal-product-name">No products available</div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="deal-stars">
                          <span className="deal-star">✨</span>
                          <span className="deal-star">⭐</span>
                          <span className="deal-star">✨</span>
                        </div>
                      </div>
                      <div className="deal-text-content">
                        <h3 className="deal-headline">{categoryName} Deals</h3>
                        {products.length > 0 && (() => {
                          // Calculate pricing information for discounted products
                          const discountedProducts = products.filter(p => (p.discount || 0) > 0);
                          const prices = products.map(p => p.price || 0).filter(p => p > 0);
                          const discounts = products.map(p => p.discount || 0).filter(d => d > 0);
                          const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                          const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
                          const maxDiscount = discounts.length > 0 
                            ? Math.max(...discounts) 
                            : 0;
                          const hasDiscounts = maxDiscount > 0;
                          const discountedCount = discountedProducts.length;
                          
                          return (
                            <>
                              {hasDiscounts && (
                                <div className="deal-discount-badge">
                                  Up to {maxDiscount}% OFF
                                </div>
                              )}
                              <p className="deal-description">
                                {hasDiscounts && prices.length > 0 ? (
                                  <>
                                    {discountedCount > 0 ? discountedCount : products.length} {discountedCount > 0 ? 'discounted' : ''} products from ₹{Math.round(minPrice).toLocaleString('en-IN')} - ₹{Math.round(maxPrice).toLocaleString('en-IN')}
                                  </>
                                ) : prices.length > 0 ? (
                                  <>
                                    {products.length} products from ₹{Math.round(minPrice).toLocaleString('en-IN')}
                                  </>
                                ) : (
                                  `Discover ${products.length} amazing products at great prices.`
                                )}
                              </p>
                            </>
                          );
                        })()}
                        {products.length === 0 && (
                          <p className="deal-description">
                            Explore our selection of {categoryName.toLowerCase()} products.
                          </p>
                        )}
                        <button 
                          type="button"
                          className="deal-link" 
                          onClick={() => { 
                            navigate('/browse-all', { 
                              state: { 
                                categoryId: categoryId, 
                                categoryName: categoryName,
                                filterType: 'category' 
                              } 
                            }); 
                          }}
                        >
                          Shop Now
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="weekly-deals-empty">
                <p>No deals available at the moment.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Your Coupons Section - Only in Dashboard */}
      {currentView === 'main' && (
        <section className="coupons-section" aria-label="Your Coupons">
          <div className="coupons-container">
            <div className="coupons-header">
              <h2 className="coupons-title">Your Coupons</h2>
              <button type="button" className="view-all-coupons-link" onClick={() => navigate('/coupons')}>
                View All Coupons →
              </button>
            </div>
            {/* Debug Info - Only show in development mode */}
            {process.env.NODE_ENV === 'development' && couponError && (
              <div style={{ 
                padding: '0.5rem 1rem', 
                marginBottom: '1rem', 
                backgroundColor: '#fef2f2', 
                borderRadius: '4px',
                fontSize: '0.75rem',
                color: '#dc2626',
                fontFamily: 'monospace',
                border: '1px solid #fecaca'
              }}>
                <strong>⚠️ Error:</strong> {couponError}
              </div>
            )}
            {/* Show error message in production too */}
            {couponError && (
              <div style={{ 
                padding: '1rem', 
                marginBottom: '1rem', 
                backgroundColor: '#fef2f2', 
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#dc2626'
              }}>
                <strong>⚠️ Error loading coupons:</strong> {couponError}
                <button 
                  onClick={() => {
                    setCouponError(null);
                    // Retry loading
                    const loadCoupons = async () => {
                      try {
                        setLoadingCoupons(true);
                        const availableCoupons = await apiService.getAvailableCoupons({
                          user_id: authUser?.id,
                        });
                        setCoupons(availableCoupons.slice(0, 5));
                        setCouponError(null);
                      } catch (error: any) {
                        setCouponError(error?.message || 'Failed to load coupons');
                      } finally {
                        setLoadingCoupons(false);
                      }
                    };
                    loadCoupons();
                  }}
                  style={{
                    marginLeft: '1rem',
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              </div>
            )}
            <div className="coupons-carousel">
              <button 
                className="carousel-nav-btn carousel-prev" 
                onClick={() => {
                  const carousel = document.querySelector('.coupons-scroll');
                  if (carousel) {
                    carousel.scrollBy({ left: -350, behavior: 'smooth' });
                  }
                }}
                aria-label="Previous coupons"
              >
                ‹
              </button>
              <div className="coupons-scroll">
                {loadingCoupons ? (
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: '100%', padding: '2rem', gap: '0.5rem' }}>
                    <div>Loading coupons...</div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Please wait</div>
                  </div>
                ) : coupons.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: '100%', padding: '2rem', gap: '0.5rem' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 500 }}>No coupons available at the moment</div>
                    {!couponError && (
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center' }}>
                        {process.env.NODE_ENV === 'development' ? (
                          <>
                            <div>✅ API call succeeded but returned 0 coupons</div>
                            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                              Check browser console (F12) for detailed API response logs
                            </div>
                            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                              Look for logs starting with 🎫 to see what the API returned
                            </div>
                          </>
                        ) : (
                          <span>Check back later for new offers!</span>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  (() => {
                    console.log('🎫🎫🎫 RENDERING COUPONS 🎫🎫🎫', {
                      totalCoupons: coupons.length,
                      userCouponsCount: userCoupons.length,
                      coupons: coupons.map(c => ({ id: c.id, code: c.code })),
                      userCoupons: userCoupons
                    });
                    return coupons.map((coupon) => {
                    const formatDiscount = (coupon: Coupon): string => {
                      if (coupon.discount_type === 'percentage') {
                        return `Save ${coupon.discount_value}%`;
                      } else if (coupon.discount_type === 'fixed') {
                        return `Save ₹${coupon.discount_value}`;
                      } else {
                        return 'Free Shipping';
                      }
                    };

                    const formatDate = (dateString: string): string => {
                      const date = new Date(dateString);
                      const today = new Date();
                      const isToday = date.toDateString() === today.toDateString();
                      if (isToday) return 'Today!';
                      return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
                    };

                    const isExpired = new Date(coupon.valid_until) < new Date();
                    const isExpiringToday = new Date(coupon.valid_until).toDateString() === new Date().toDateString();
                    
                    // Check if coupon is clipped - handle multiple response formats and string comparison
                    const isClipped = userCoupons.some((uc: any) => {
                      // Try different possible ID fields from user_coupons response
                      const clippedCouponId = uc.coupon_id || uc.coupon?.id || (uc.coupon && uc.coupon.id);
                      const currentCouponId = coupon.id;
                      
                      // Compare as strings to handle any type mismatches
                      const matches = String(clippedCouponId) === String(currentCouponId);
                      
                      if (matches) {
                        console.log('✅ Found clipped coupon match:', { 
                          couponId: currentCouponId, 
                          clippedCouponId,
                          ucStructure: Object.keys(uc)
                        });
                      }
                      return matches;
                    });
                    
                    // Debug logging - ALWAYS log to help troubleshoot
                    console.log('🎫🎫🎫 COUPON RENDER CHECK 🎫🎫🎫', {
                      couponId: coupon.id,
                      couponCode: coupon.code,
                      isClipped: isClipped,
                      isExpired: isExpired,
                      userCouponsCount: userCoupons.length,
                      userCoupons: userCoupons,
                      userCouponDetails: userCoupons.map((uc: any) => ({
                        coupon_id: uc.coupon_id,
                        has_coupon_object: !!uc.coupon,
                        nested_coupon_id: uc.coupon?.id,
                        full_structure: uc
                      })),
                      willShowBadge: isClipped && !isExpired
                    });

                    return (
                      <div key={coupon.id} className="coupon-card" style={{ position: 'relative' }}>
                        {/* Main Clipped Badge - Show if clipped and not expired */}
                        {isClipped && !isExpired && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                            pointerEvents: 'none',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            border: '2px solid white'
                          }}>
                            ✓ Clipped
                          </div>
                        )}
                        {/* Debug badge - ALWAYS show to help troubleshoot */}
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          backgroundColor: isClipped ? '#10b981' : '#fbbf24',
                          color: isClipped ? 'white' : 'black',
                          padding: '4px 6px',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          zIndex: 1001,
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          border: '1px solid rgba(0,0,0,0.2)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {isClipped ? '✓ CLIPPED' : 'NOT CLIPPED'}
                        </div>
                        <div className="coupon-image-container">
                          <div style={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            backgroundColor: '#f9fafb',
                            fontSize: '3rem'
                          }}>
                            🎫
                          </div>
                        </div>
                        <div className="coupon-offer">{formatDiscount(coupon)}</div>
                        <div className="coupon-description">
                          {coupon.description || coupon.title || `Use code: ${coupon.code}`}
                        </div>
                        <div className="coupon-expiration">
                          Exp. {formatDate(coupon.valid_until)}
                          {isExpiringToday && !isExpired && (
                            <span className="expiration-highlight"> - Today!</span>
                          )}
                        </div>
                        <div className="coupon-buttons">
                          <button 
                            className="coupon-shop-btn" 
                            onClick={() => navigate('/browse-all')}
                          >
                            Shop All Items
                          </button>
                          <button 
                            className="coupon-clip-btn" 
                            onClick={async () => {
                              if (!isAuthenticated || !authUser) {
                                navigate('/login', { state: { returnTo: '/app' } });
                                return;
                              }
                              try {
                                if (isClipped) {
                                  // If already clipped, remove it
                                  await apiService.removeClippedCoupon(coupon.id, authUser.id);
                                  alert('Coupon removed from your saved coupons');
                                } else {
                                  // Clip the coupon
                                  await apiService.clipCoupon(coupon.id, authUser.id);
                                  alert('Coupon clipped successfully!');
                                }
                                
                                // Reload user's clipped coupons FIRST to update badge
                                console.log('🔄 Reloading user coupons after clip/remove...');
                                const clipped = await apiService.getUserCoupons(authUser.id);
                                console.log('🔄 Reloaded user coupons:', clipped);
                                console.log('🔄 User coupon structures:', clipped.map((uc: any) => ({
                                  coupon_id: uc.coupon_id,
                                  has_coupon_object: !!uc.coupon,
                                  nested_coupon_id: uc.coupon?.id,
                                  all_keys: Object.keys(uc),
                                  full_object: uc
                                })));
                                
                                // Update state IMMEDIATELY with new array reference to force re-render
                                console.log('🔄 Updating state immediately with', clipped.length, 'clipped coupons');
                                setUserCoupons([...clipped]);
                                
                                // Also reload available coupons (in case they change)
                                const availableCoupons = await apiService.getAvailableCoupons({
                                  user_id: authUser.id,
                                });
                                setCoupons([...availableCoupons.slice(0, 5)]);
                                
                                // Debug: Check if the clipped coupon is in the response
                                console.log('🔄 Verifying clipped coupon in response...');
                                const foundCoupon = clipped.find((uc: any) => {
                                  const id = uc.coupon_id || uc.coupon?.id || uc.id;
                                  return String(id) === String(coupon.id);
                                });
                                console.log('🔄 Found clipped coupon?', !!foundCoupon, foundCoupon);
                                console.log('🔄 State should update now - check UI');
                              } catch (error: any) {
                                console.error('❌ Error clipping/removing coupon:', error);
                                alert(error.message || 'Failed to clip coupon');
                              }
                            }}
                            disabled={isExpired}
                          >
                            {isClipped ? 'Remove' : 'Clip Coupon'}
                          </button>
                        </div>
                      </div>
                    );
                  });
                  })()
                )}
              </div>
              <button 
                className="carousel-nav-btn carousel-next" 
                onClick={() => {
                  const carousel = document.querySelector('.coupons-scroll');
                  if (carousel) {
                    carousel.scrollBy({ left: 350, behavior: 'smooth' });
                  }
                }}
                aria-label="Next coupons"
              >
                ›
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Services Section - Show in Dashboard */}
      {currentView === 'main' && (
        <section className="services-categories-section" aria-label="Services">
          <div className="services-container">
            <div className="services-header">
              <h2 className="services-section-title">
                {language === 'hi' ? 'स्थानीय सेवाएं' : language === 'te' ? 'స్థానిక సేవలు' : language === 'ta' ? 'உள்ளூர் சேவைகள்' : 'Local Services'}
              </h2>
              <button type="button" className="view-all-services-link" onClick={() => navigate('/services')}>
                View All Services →
              </button>
            </div>
            <div className="services-carousel">
              <button
                className="carousel-nav-btn carousel-prev"
                onClick={() => {
                  const carousel = document.querySelector('.services-scroll');
                  if (carousel) {
                    carousel.scrollBy({ left: -400, behavior: 'smooth' });
                  }
                }}
                aria-label="Previous services"
              >
                ‹
              </button>
              <div className="services-carousel-wrapper">
                <div className="services-scroll">
              {/* Home & Repair Services */}
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/plumbing')}
                style={{ '--category-color': '#2563eb' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' }}>
                  <span className="service-icon-emoji">🔧</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'प्लंबिंग' : 'Plumbing'}</h3>
                  <p className="service-category-desc">Pipe repair, installation, water issues</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/electrical')}
                style={{ '--category-color': '#f59e0b' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                  <span className="service-icon-emoji">⚡</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'इलेक्ट्रिकल' : 'Electricals'}</h3>
                  <p className="service-category-desc">Wiring, switches, electrical repair</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/painting')}
                style={{ '--category-color': '#10b981' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                  <span className="service-icon-emoji">🎨</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'पेंटिंग' : 'Painting'}</h3>
                  <p className="service-category-desc">Home painting, wall colors</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/carpenter')}
                style={{ '--category-color': '#8b5cf6' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                  <span className="service-icon-emoji">🪚</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'बढ़ई' : 'Carpenter'}</h3>
                  <p className="service-category-desc">Furniture, doors, windows</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/mason')}
                style={{ '--category-color': '#ef4444' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                  <span className="service-icon-emoji">🧱</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'राजमिस्त्री' : 'Mason / Building'}</h3>
                  <p className="service-category-desc">Construction, building work</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/tiles-flooring')}
                style={{ '--category-color': '#06b6d4' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                  <span className="service-icon-emoji">🔲</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'टाइल्स' : 'Tiles & Flooring'}</h3>
                  <p className="service-category-desc">Tile installation, flooring</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/waterproofing')}
                style={{ '--category-color': '#3b82f6' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                  <span className="service-icon-emoji">💧</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'वॉटरप्रूफिंग' : 'Waterproofing'}</h3>
                  <p className="service-category-desc">Roof, terrace waterproofing</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/pest-control')}
                style={{ '--category-color': '#84cc16' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)' }}>
                  <span className="service-icon-emoji">🐛</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'कीट नियंत्रण' : 'Pest Control'}</h3>
                  <p className="service-category-desc">Rodent, insect control</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/ac-repair')}
                style={{ '--category-color': '#0ea5e9' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}>
                  <span className="service-icon-emoji">❄️</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'एसी/फ्रिज रिपेयर' : 'AC / Fridge Repair'}</h3>
                  <p className="service-category-desc">AC, fridge, washing machine</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/home-cleaning')}
                style={{ '--category-color': '#06b6d4' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                  <span className="service-icon-emoji">🧹</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'घर की सफाई' : 'Home Cleaning'}</h3>
                  <p className="service-category-desc">Deep cleaning, housekeeping</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/beautician')}
                style={{ '--category-color': '#ec4899' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' }}>
                  <span className="service-icon-emoji">💄</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'ब्यूटीशियन' : 'Beautician'}</h3>
                  <p className="service-category-desc">Makeup, beauty services</p>
                </div>
              </div>
              <div 
                className="service-category-card"
                onClick={() => navigate('/services/tailor')}
                style={{ '--category-color': '#f97316' } as React.CSSProperties}
              >
                <div className="service-category-icon" style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}>
                  <span className="service-icon-emoji">✂️</span>
                </div>
                <div className="service-category-info">
                  <h3 className="service-category-name">{language === 'hi' ? 'दर्जी' : 'Tailor'}</h3>
                  <p className="service-category-desc">Stitching, alterations</p>
                </div>
              </div>
                </div>
              </div>
              <button
                className="carousel-nav-btn carousel-next"
                onClick={() => {
                  const carousel = document.querySelector('.services-scroll');
                  if (carousel) {
                    carousel.scrollBy({ left: 400, behavior: 'smooth' });
                  }
                }}
                aria-label="Next services"
              >
                ›
              </button>
            </div>
            <div className="services-onboard-section" style={{ marginTop: '48px' }}>
              <div className="services-container">
                <div className="services-onboard-card">
                  <div className="services-onboard-content">
                    <h3 className="services-onboard-title">
                      {language === 'hi' ? 'सेवा प्रदाता बनें' : language === 'te' ? 'సేవా ప్రదాత అవ్వండి' : language === 'ta' ? 'சேவை வழங்குநராக இணையுங்கள்' : 'Become a Service Provider'}
                    </h3>
                    <p className="services-onboard-desc">
                      {language === 'hi' 
                        ? 'अपनी सेवाएं सूचीबद्ध करें और ग्राहकों तक पहुंचें'
                        : language === 'te'
                        ? 'మీ సేవలను జాబితా చేసి కస్టమర్లకు చేరుకోండి'
                        : language === 'ta'
                        ? 'உங்கள் சேவைகளை பட்டியலிடுங்கள் மற்றும் வாடிக்கையாளர்களை அடையுங்கள்'
                        : 'List your services and reach customers'}
                    </p>
                    <button
                      className="services-onboard-btn"
                      onClick={() => navigate('/services/onboard')}
                    >
                      {language === 'hi' ? 'अभी शुरू करें' : language === 'te' ? 'ఇప్పుడే ప్రారంభించండి' : language === 'ta' ? 'இப்போதே தொடங்குங்கள்' : 'Get Started'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* We Believe In Section - Only in Dashboard */}
      {currentView === 'main' && (
        <section className="we-believe-section" aria-label="We Believe In">
          <div className="we-believe-container">
            <h2 className="we-believe-title">
              <span className="we-believe-we">We</span>
              <span className="we-believe-highlight">Believe In</span>
            </h2>
            
            <div className="we-believe-grid">
              {/* Farm/Harvest Panel */}
              <div className="believe-panel">
                <div className="believe-panel-image farm-panel">
                  <div className="panel-overlay">
                    <div className="panel-icon">🌾</div>
                    <h3 className="panel-title">Fresh from the Farm</h3>
                    <p className="panel-description">Locally sourced produce, harvested at peak freshness for your table</p>
                  </div>
                </div>
              </div>

              {/* Grocery Delivery/Pickup Panel */}
              <div className="believe-panel">
                <div className="believe-panel-image delivery-panel">
                  <div className="panel-overlay">
                    <div className="panel-icon">📦</div>
                    <h3 className="panel-title">Convenient Delivery</h3>
                    <p className="panel-description">Fresh groceries delivered to your door, or ready for pickup</p>
                  </div>
                </div>
              </div>

              {/* Healthy Living Panel */}
              <div className="believe-panel">
                <div className="believe-panel-image healthy-panel">
                  <div className="panel-overlay">
                    <div className="panel-icon">🥤</div>
                    <h3 className="panel-title">Healthy Choices</h3>
                    <p className="panel-description">Quality ingredients for nutritious meals and healthy living</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content Area */}
      <main id="main-content" className="main-content" role="main">

        {/* Content based on currentView */}
        
        {currentView === 'discovery' && (
          <>
          <ShopDiscovery
            onSelectShop={(shop) => {
              setSelectedShopForDetails(shop);
              setCurrentView('browser');
            }}
            onViewAllShops={() => setCurrentView('browser')}
            onViewShopDetails={handleViewShopDetails}
          />
          </>
        )}
        
        {currentView === 'browser' && (
          <MultiShopBrowser
            onAddToCart={(product, shop) => {
              // Add to multi-shop cart
              const existingItem = multiShopCart.find(item => 
                item.product.id === product.id && item.shop.id === shop.id
              );
              
              if (existingItem) {
                setMultiShopCart(prev => 
                  prev.map(item => 
                    item.product.id === product.id && item.shop.id === shop.id
                      ? { ...item, quantity: item.quantity + 1 }
                      : item
                  )
                );
              } else {
                setMultiShopCart(prev => [...prev, { product, shop, quantity: 1 }]);
              }
            }}
            onViewShop={handleViewShopDetails}
            cartItems={multiShopCart}
            initialShopId={(location.state as any)?.shopId}
          />
        )}
        
        {currentView === 'search' && (
          <div>
            <AdvancedSearch
              onResults={(results) => {
                setSearchResults(results);
              }}
              onLoading={(loading) => {
                setIsSearchLoading(loading);
              }}
              onError={(error) => {
                setSearchError(error);
              }}
            />
            {searchResults.length > 0 && (
              <SearchResults
                results={searchResults}
                loading={isSearchLoading}
                error={searchError}
                onAddToCart={(product, shop) => {
                  // Add to multi-shop cart
                  const existingItem = multiShopCart.find(item => 
                    item.product.id === product.id && item.shop.id === shop.id
                  );
                  
                  if (existingItem) {
                    setMultiShopCart(prev => 
                      prev.map(item => 
                        item.product.id === product.id && item.shop.id === shop.id
                          ? { ...item, quantity: item.quantity + 1 }
                          : item
                      )
                    );
                  } else {
                    setMultiShopCart(prev => [...prev, { product, shop, quantity: 1 }]);
                  }
                }}
                onViewShop={handleViewShopDetails}
                cartItems={multiShopCart}
              />
            )}
          </div>
        )}
        
        {currentView === 'cart' && (() => {
          console.log('🛒 App: Rendering cart view');
          console.log('🛒 App: multiShopCart state:', multiShopCart);
          console.log('🛒 App: multiShopCart length:', multiShopCart.length);
          console.log('🛒 App: multiShopCart items:', multiShopCart.map(item => ({
            productName: item.product?.name,
            shopName: item.shop?.name,
            quantity: item.quantity
          })));
          
          // If cart is empty but header shows items, force reload
          if (multiShopCart.length === 0) {
            console.warn('⚠️ App: Cart state is empty but view is open, forcing reload...');
            // Force immediate reload
            setTimeout(() => {
              loadCartFromStorage();
            }, 0);
            setTimeout(() => {
              loadCartFromStorage();
            }, 200);
            setTimeout(() => {
              loadCartFromStorage();
            }, 500);
          }
          
          return (
            <CrossShopCart
              items={multiShopCart}
              savedItems={savedForLater}
            onUpdateQuantity={(productId, shopId, quantity) => {
              setMultiShopCart(prev => 
                prev.map(item => 
                  item.product.id === productId && item.shop.id === shopId
                    ? { ...item, quantity }
                    : item
                ).filter(item => item.quantity > 0)
              );
            }}
            onRemoveItem={(productId, shopId) => {
              setMultiShopCart(prev => 
                prev.filter(item => 
                  !(item.product.id === productId && item.shop.id === shopId)
                )
              );
            }}
            onSaveForLater={handleSaveForLater}
            onMoveToCart={handleMoveToCart}
            onRemoveFromSaved={handleRemoveFromSaved}
            onContinueShopping={() => {
              setCurrentView('main');
              navigate('/browse-all');
            }}
            onCheckout={handleMultiShopCheckout}
          />
          );
        })()}
        
      </main>

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="notifications-panel">
          <h3>{t.notifications}</h3>
          {notifications.map(notification => (
            <div key={notification.id} className={`notification-item ${notification.type}`}>
              {notification.message}
            </div>
          ))}
        </div>
      )}

      {/* Shopping Cart Sidebar */}
      {showCart && (
        <div className="cart-sidebar">
          <div className="cart-header">
            <h3>🛒 Shopping Cart</h3>
            <button onClick={() => setShowCart(false)} className="close-btn">✕</button>
          </div>
          {cart.length === 0 ? (
            <p className="empty-cart">Your cart is empty</p>
          ) : (
            <>
              <div className="cart-items">
                {cart.map(item => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-image">{item.image}</div>
                    <div className="cart-item-details">
                      <h4>{item.name}</h4>
                      {item.brand && <p className="cart-item-brand">{item.brand}</p>}
                      <p>${item.price}</p>
                      <div className="quantity-controls">
                        <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                      </div>
                    </div>
                    <button onClick={() => removeFromCart(item.id)} className="remove-btn">🗑️</button>
                  </div>
                ))}
              </div>
              <div className="cart-footer">
                <div className="cart-total">
                  <strong>Total: ${cartTotal.toFixed(2)}</strong>
                </div>
                <button className="checkout-btn" onClick={handleCheckout}>Proceed to Checkout</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">



              {currentView === 'order' && showOrderPlacement && (
                <OrderPlacement
                  shopOrders={Object.values(
                    multiShopCart.reduce((acc, item) => {
                      if (!acc[item.shop.id]) {
                        acc[item.shop.id] = { shop: item.shop, items: [] };
                      }
                      acc[item.shop.id].items.push(item);
                      return acc;
                    }, {} as { [shopId: string]: { shop: Shop; items: MultiShopCartItem[] } })
                  )}
                  onOrderPlaced={handleOrderPlaced}
                  onCancel={handleOrderCancel}
                />
              )}

              {currentView === 'search' && (
                <div className="search-view">
                  <AdvancedSearch
                    onResults={handleSearchResults}
                    onLoading={handleSearchLoading}
                    onError={handleSearchError}
                  />
                  <SearchResults
                    results={searchResults}
                    onAddToCart={handleAddToMultiShopCart}
                    onViewShop={handleViewShopDetails}
                    loading={isSearchLoading}
                    error={searchError}
                    cartItems={multiShopCart}
                  />
                </div>
              )}

        {/* Dashboard View */}
        {currentView === 'main' && (
          <div className="main-store-container">
            {/* Dashboard content can go here if needed */}
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer />

      {/* Address Modal */}
      <AddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSelectAddress={(address) => {
          setDeliveryAddress(address);
          localStorage.setItem('delivery_address', address);
        }}
        currentAddress={deliveryAddress}
      />

      {/* AI Assistant Integration */}
      <AIAssistantIntegration 
        userId={isAuthenticated && authUser ? authUser.id : "guest_user"}
        language={language}
      />

      {/* Shop Details Modal */}
      {selectedShopForDetails && (
        <ShopDetails
          shop={selectedShopForDetails}
          onClose={handleCloseShopDetails}
          onSelectShop={(_shop) => {
            setCurrentView('browser');
            handleCloseShopDetails();
          }}
        />
      )}

      {/* Floating Search Button for Mobile */}
      <button 
        className="floating-search-btn"
        onClick={() => setShowFloatingSearch(!showFloatingSearch)}
        aria-label="Open search"
      >
        🔍
      </button>

      {/* Floating Search Panel */}
      <div className={`floating-search-panel ${showFloatingSearch ? 'active' : ''}`}>
        <input
          type="text"
          className="search-input"
          placeholder="Search shops, products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
              setShowFloatingSearch(false);
            }
          }}
        />
        <div className="search-actions">
          <button 
            className="close-btn"
            onClick={() => setShowFloatingSearch(false)}
          >
            {t.cancel || 'Cancel'}
          </button>
          <button 
            className="search-btn"
            onClick={() => {
              handleSearch();
              setShowFloatingSearch(false);
            }}
          >
            {t.search || 'Search'}
          </button>
        </div>
      </div>

      {/* Quick Actions Toggle Button */}
      <button 
        className={`quick-actions-toggle ${showQuickActions ? 'active' : ''}`}
        onClick={() => setShowQuickActions(!showQuickActions)}
        aria-label="Quick actions"
      >
        ⚡
      </button>

      {/* Quick Actions Panel */}
      <div className={`quick-actions-panel ${showQuickActions ? 'active' : ''}`}>
        <h3>Quick Actions</h3>
        <button 
          className="quick-action-btn"
          onClick={() => {
            setCurrentView('discovery');
            setShowQuickActions(false);
          }}
        >
          <span className="icon">🏪</span>
          {t.exploreShops || 'Explore Shops'}
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => {
            setCurrentView('browser');
            setShowQuickActions(false);
          }}
        >
          <span className="icon">🛍️</span>
          Browse All
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => {
            setCurrentView('cart');
            setShowQuickActions(false);
          }}
        >
          <span className="icon">🛒</span>
          View Cart ({multiShopCart.length})
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => {
            setShowFloatingSearch(true);
            setShowQuickActions(false);
          }}
        >
          <span className="icon">🔍</span>
          Search
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => {
            setCurrentView('main');
            setShowQuickActions(false);
          }}
        >
          <span className="icon">🏠</span>
          Home
        </button>
        <button 
          className="quick-action-btn"
          onClick={() => {
            setCompactMode(!compactMode);
            setShowQuickActions(false);
          }}
        >
          <span className="icon">{compactMode ? '📏' : '📐'}</span>
          {compactMode ? 'Normal View' : 'Compact View'}
        </button>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        <div className="mobile-nav-items">
          <button 
            className={`mobile-nav-item ${currentView === 'discovery' ? 'active' : ''}`}
            onClick={() => setCurrentView('discovery')}
          >
            <span className="icon">🏪</span>
            <span className="label">Explore</span>
          </button>
          <button
            className={`mobile-nav-item ${location.pathname === '/browse-all' || location.pathname === '/browse' ? 'active' : ''}`}
            onClick={() => navigate('/browse-all')}
          >
            <span className="icon">🛍️</span>
            <span className="label">Browse</span>
          </button>
          <button 
            className={`mobile-nav-item ${currentView === 'search' ? 'active' : ''}`}
            onClick={() => setCurrentView('search')}
          >
            <span className="icon">🔍</span>
            <span className="label">Search</span>
          </button>
          <button 
            className={`mobile-nav-item ${currentView === 'cart' ? 'active' : ''}`}
            onClick={() => setCurrentView('cart')}
          >
            <span className="icon">🛒</span>
            <span className="label">Cart</span>
            {multiShopCart.length > 0 && (
              <span className="badge">{multiShopCart.length}</span>
            )}
          </button>
          <button 
            className={`mobile-nav-item ${currentView === 'main' ? 'active' : ''}`}
            onClick={() => setCurrentView('main')}
          >
            <span className="icon">🏠</span>
            <span className="label">Home</span>
          </button>
        </div>
      </div>

      {/* Shop Selection Modal for Popular Products */}
      <ShopSelectionModal
        isOpen={showShopModal}
        product={selectedProductForCart}
        onClose={() => {
          setShowShopModal(false);
          setSelectedProductForCart(null);
        }}
        onSelectShop={(shop, product) => {
          handleShopSelectFromPopular(shop, product as ApiProduct);
        }}
      />
    </div>
  );
}

export default App; 